import { useAuthStore, auth } from '../stores/auth-store';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.rumio.world';

interface RequestOptions extends RequestInit {
  requireAuth?: boolean;
  includeAuthIfAvailable?: boolean; // Send auth header if logged in, but don't require it
  isFormData?: boolean;
}

// Marketplace Types
interface MarketplaceListing {
  id: string;
  seller_id: string;
  product_code: string;
  asking_price: number;
  retail_price: number | null;
  condition: 'excellent' | 'good' | 'fair';
  assembly_status: 'flatpack_sealed' | 'flatpack_opened' | 'assembled' | 'disassembled';
  times_assembled: number;
  missing_parts: string | null;
  modifications: string | null;
  description: string | null;
  photos: string[];
  postal_code: string | null;
  neighborhood: string | null;
  country: string | null;
  location: string | null;
  status: 'active' | 'pending' | 'sold' | 'expired' | 'removed';
  is_discontinued: boolean;
  views_count: number;
  inquiries_count: number;
  created_at: string;
  updated_at: string;
  sold_at: string | null;
}

interface MarketplaceConversation {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  status: 'active' | 'archived';
  buyer_unread_count: number;
  seller_unread_count: number;
  last_message_at: string | null;
  created_at: string;
  listing?: Partial<MarketplaceListing>;
  other_party?: { id: string; email: string };
  last_message_content?: string;
  last_message_type?: string;
  is_seller?: boolean;
  unread_count?: number;
}

interface MarketplaceMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'system';
  read_at: string | null;
  created_at: string;
}

interface MarketplaceTransaction {
  id: string;
  listing_id: string;
  conversation_id: string | null;
  buyer_id: string;
  seller_id: string;
  agreed_price: number;
  meetup_location: string | null;
  meetup_time: string | null;
  meetup_notes: string | null;
  status: 'pending' | 'completed' | 'cancelled' | 'no_show';
  seller_confirmed_at: string | null;
  buyer_confirmed_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
  listing?: Partial<MarketplaceListing>;
  other_party?: { id: string; email: string };
  is_seller?: boolean;
  other_party_id?: string;
}

interface MarketplaceRating {
  id: string;
  transaction_id: string;
  rater_id: string;
  rated_id: string;
  item_accuracy_rating: number | null;
  communication_rating: number | null;
  reliability_rating: number | null;
  overall_rating: number;
  comment: string | null;
  created_at: string;
}

interface MarketplaceUserStats {
  total_sales: number;
  total_purchases: number;
  seller_avg_overall: number | null;
  buyer_avg_overall: number | null;
  seller_rating_count?: number;
  buyer_rating_count?: number;
}

interface MarketplacePreferences {
  id: string;
  user_id: string;
  search_radius_km: number;
  search_location: string | null;
  search_postal_code: string | null;
  search_country: string | null;
  seller_location: string | null;
  seller_postal_code: string | null;
  seller_neighborhood: string | null;
  accept_excellent: boolean;
  accept_good: boolean;
  accept_fair: boolean;
  accept_flatpack_only: boolean;
  accept_assembled: boolean;
  notify_new_matches: boolean;
  notify_price_drops: boolean;
  notify_messages: boolean;
  created_at: string;
  updated_at: string;
}




// SMCC Types for API responses
// Platform identifiers
type SMCCPlatform =
  | 'instagram'
  | 'tiktok'
  | 'twitter'
  | 'facebook'
  | 'pinterest'
  | 'youtube'
  | 'linkedin'
  | 'threads'
  | 'snapchat'
  | 'reddit'
  | 'whatsapp'
  | 'telegram';

type SMCCContentType =
  | 'post'
  | 'story'
  | 'reel'
  | 'video'
  | 'short'
  | 'pin'
  | 'tweet'
  | 'thread'
  | 'carousel'
  | 'community_post'
  | 'article'
  | 'poll';

type SMCCContentStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'archived'
  | 'rejected';

type SMCCAutomationLevel = 'manual' | 'human_approval' | 'fully_automated';

interface SMCCMediaItem {
  url: string;
  type: 'image' | 'video' | 'gif';
  alt_text?: string;
  thumbnail_url?: string;
  dimensions?: { width: number; height: number };
  duration_seconds?: number;
}

interface SMCCContent {
  id: string;
  content_type: SMCCContentType;
  title?: string;
  body?: string;
  body_localized?: Record<string, string>;
  media_urls: SMCCMediaItem[];
  thumbnail_url?: string;
  target_platforms: SMCCPlatform[];
  target_regions?: string[];
  target_languages?: string[];
  status: SMCCContentStatus;
  automation_level: SMCCAutomationLevel;
  scheduled_at?: string;
  published_at?: string;
  expires_at?: string;
  campaign_id?: string;
  template_id?: string;
  brand_voice_id?: string;
  created_by?: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  approval_notes?: string;
  ai_generated?: boolean;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_impressions: number;
  total_reach: number;
  engagement_rate?: number;
  created_at: string;
  updated_at: string;
}

interface SMCCContentVersion {
  id: string;
  content_id: string;
  platform: SMCCPlatform;
  account_id: string;
  body?: string;
  hashtags?: string[];
  mentions?: string[];
  media_urls: SMCCMediaItem[];
  link_url?: string;
  platform_post_id?: string;
  post_url?: string;
  status: string;
  error_message?: string;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  impressions: number;
  reach: number;
  clicks: number;
  engagement_rate?: number;
  scheduled_at?: string;
  published_at?: string;
  created_at: string;
}

type SMCCCampaignStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'archived';

type SMCCCampaignGoalType =
  | 'awareness'
  | 'engagement'
  | 'traffic'
  | 'conversions'
  | 'followers'
  | 'sales';

interface SMCCCampaign {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  goal_type?: SMCCCampaignGoalType;
  target_metrics?: Record<string, number>;
  budget?: number;
  spent: number;
  currency: string;
  target_platforms?: SMCCPlatform[];
  target_regions?: string[];
  status: SMCCCampaignStatus;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

type SMCCInboxItemType = 'comment' | 'dm' | 'mention' | 'reply' | 'review';

type SMCCSentiment = 'positive' | 'negative' | 'neutral' | 'mixed';

type SMCCIntent =
  | 'question'
  | 'complaint'
  | 'praise'
  | 'inquiry'
  | 'spam'
  | 'support_request'
  | 'purchase_intent'
  | 'feedback'
  | 'general'
  | 'urgent';

type SMCCPriority = 'low' | 'normal' | 'high' | 'urgent';

type SMCCInboxItemStatus =
  | 'unread'
  | 'read'
  | 'in_progress'
  | 'replied'
  | 'archived'
  | 'spam'
  | 'flagged'
  | 'escalated';

interface SMCCInboxItem {
  id: string;
  account_id: string;
  item_type: SMCCInboxItemType;
  platform_item_id: string;
  sender_id: string;
  sender_username?: string;
  sender_display_name?: string;
  sender_profile_image?: string;
  sender_follower_count?: number;
  sender_is_verified?: boolean;
  content: string;
  content_language?: string;
  media_urls: SMCCMediaItem[];
  conversation_id?: string;
  sentiment?: SMCCSentiment;
  sentiment_score?: number;
  intent?: SMCCIntent;
  topics?: string[];
  priority: SMCCPriority;
  requires_response: boolean;
  status: SMCCInboxItemStatus;
  assigned_to?: string;
  responded_at?: string;
  responded_by?: string;
  response_content?: string;
  received_at: string;
  read_at?: string;
  created_at: string;
  smcc_accounts?: { platform: string; name: string };
  suggested_responses?: SMCCSuggestedResponse[];
}

interface SMCCSuggestedResponse {
  text: string;
  confidence: number;
  tone: string;
}

interface SMCCAutoResponseRule {
  id: string;
  name: string;
  description?: string;
  trigger_conditions: Record<string, unknown>;
  response_type: string;
  response_template?: string;
  platforms?: string[];
  accounts?: string[];
  item_types?: SMCCInboxItemType[];
  delay_seconds: number;
  priority: number;
  is_active: boolean;
  total_triggers: number;
  last_triggered_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface SMCCModerationLog {
  id: string;
  inbox_item_id: string;
  action: string;
  performed_by?: string;
  reason?: string;
  details?: Record<string, unknown>;
  created_at: string;
}

type SMCCAssetType = 'image' | 'video' | 'gif' | 'audio' | 'document';

interface SMCCAsset {
  id: string;
  asset_type: SMCCAssetType;
  file_url: string;
  thumbnail_url?: string;
  preview_url?: string;
  name?: string;
  description?: string;
  alt_text?: string;
  tags: string[];
  caption?: string;
  file_size?: number;
  mime_type?: string;
  dimensions?: { width: number; height: number };
  duration_seconds?: number;
  folder_path?: string;
  source: string;
  original_url?: string;
  attribution?: string;
  use_count: number;
  last_used_at?: string;
  processing_status: 'uploading' | 'processing' | 'ready' | 'failed';
  processing_error?: string;
  is_archived: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

type SMCCConsentStatus =
  | 'pending'
  | 'requested'
  | 'reminder_sent'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'revoked';

interface SMCCUGCSubmission {
  id: string;
  platform: SMCCPlatform;
  platform_post_id: string;
  post_url: string;
  creator_username: string;
  creator_display_name?: string;
  creator_profile_url?: string;
  creator_profile_image?: string;
  creator_follower_count?: number;
  creator_is_verified?: boolean;
  content_type: 'image' | 'video' | 'carousel' | 'story' | 'reel';
  caption?: string;
  media_urls: SMCCMediaItem[];
  hashtags?: string[];
  detected_via?: string;
  detected_at: string;
  category?: string;
  quality_score?: number;
  consent_status: SMCCConsentStatus;
  is_featured: boolean;
  featured_count: number;
  featured_on_platforms?: SMCCPlatform[];
  review_status: 'pending' | 'approved' | 'rejected' | 'flagged';
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

interface SMCCComplianceRule {
  id: string;
  name: string;
  description?: string;
  rule_type: string;
  config: Record<string, unknown>;
  platforms?: string[];
  content_types?: string[];
  violation_action: string;
  severity: string;
  trigger_count: number;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface SMCCKillSwitch {
  id: string;
  scope: 'global' | 'platform' | 'account' | 'campaign';
  scope_id?: string;
  platform?: SMCCPlatform;
  is_active: boolean;
  reason: string;
  activated_by?: string;
  activated_at?: string;
  created_at: string;
}

interface SMCCCalendarItem {
  id: string;
  title?: string;
  content_type: SMCCContentType;
  status: SMCCContentStatus;
  target_platforms: SMCCPlatform[];
  scheduled_at?: string;
  published_at?: string;
  campaign_id?: string;
  campaign_name?: string;
  automation_level: SMCCAutomationLevel;
  created_by?: string;
}

interface SMCCAnalyticsDaily {
  date: string;
  followers: number;
  followers_change: number;
  impressions: number;
  reach: number;
  engagement: number;
  likes: number;
  comments: number;
  shares: number;
  posts_published: number;
}

interface SMCCTopContent {
  id: string;
  title?: string;
  content_type: SMCCContentType;
  platform: SMCCPlatform;
  published_at?: string;
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
  reach: number;
  engagement_rate?: number;
}

interface SMCCExportDataItem {
  date: string;
  platform?: string;
  account_name?: string;
  followers?: number;
  impressions?: number;
  reach?: number;
  engagement?: number;
  likes?: number;
  comments?: number;
  shares?: number;
}

interface SMCCConversationMessage {
  id: string;
  sender_id: string;
  sender_username?: string;
  content: string;
  media_urls?: SMCCMediaItem[];
  sent_at: string;
  is_from_brand: boolean;
}

interface SMCCBrandVoice {
  id: string;
  name: string;
  description?: string;
  tone: string;
  personality_traits: string[];
  vocabulary_preferences: { prefer?: string[]; avoid?: string[] };
  emoji_usage: string;
  hashtag_style: string;
  sample_posts?: string[];
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

// SuperTokens handles token refresh automatically
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get FAQs for a specific page and language
   * TODO: Implement proper API integration
   */
  async getFaqs(_params: { page: string; lang: string }): Promise<{ data: Array<{ id: string; question: string; answer: string }> }> {
    // Stub implementation - returns empty array
    void _params; // Mark parameter as used to satisfy TypeScript
    return { data: [] };
  }

  /**
   * Get access token from SuperTokens
   */
  private async getToken(): Promise<string | null> {
    try {
      return await auth.getAccessToken();
    } catch {
      return null;
    }
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { requireAuth = false, includeAuthIfAvailable = false, isFormData = false, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      // Don't set Content-Type for FormData - browser will set it with boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...fetchOptions.headers,
    };

    // Add auth header if available and requested (even if not required)
    if (includeAuthIfAvailable && !requireAuth) {
      const accessToken = await this.getToken();
      if (accessToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    if (requireAuth) {
      const accessToken = await this.getToken();

      if (!accessToken) {
        throw new Error('Authentication required');
      }

      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;

      const response = await fetch(url, { ...fetchOptions, headers });

      // SuperTokens handles token refresh automatically via session interceptor
      // If we still get 401, the session is truly expired
      if (response.status === 401) {
        const { logout } = useAuthStore.getState();
        logout();
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || 'Request failed');
      }

      return response.json();
    }

    const response = await fetch(url, { ...fetchOptions, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        subscription_tier: 'free' | 'pro' | 'pro_plus';
        created_at: string;
        is_admin?: boolean;
        role?: 'super_admin' | 'admin' | 'moderator' | 'support' | 'content_manager' | 'user';
      };
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async signup(email: string, password: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        subscription_tier: 'free' | 'pro' | 'pro_plus';
        created_at: string;
      };
    }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async googleAuth(idToken: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        subscription_tier: 'free' | 'pro' | 'pro_plus';
        created_at: string;
      };
    }>('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    });
  }

  async appleAuth(idToken: string, nonce: string) {
    return this.request<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        subscription_tier: 'free' | 'pro' | 'pro_plus';
        created_at: string;
      };
    }>('/api/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken, nonce }),
    });
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // User endpoints
  async getProfile() {
    return this.request<{
      user: {
        id: string;
        email: string;
        subscription_tier: string;
        subscription_source?: string;
        created_at: string;
      };
    }>('/api/user/me', { requireAuth: true });
  }

  async updateProfile(data: { email?: string }) {
    return this.request<{ user: { id: string; email: string } }>('/api/user/me', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/api/user/password', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  async deleteAccount() {
    return this.request<{ message: string }>('/api/user/account', {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Subscription endpoints
  async getSubscription() {
    return this.request<{
      subscription: {
        tier: string;
        source: string;
        status: string;
        current_period_end?: string;
      };
    }>('/api/subscription', { requireAuth: true });
  }

  async createCheckoutSession(tier: 'pro' | 'pro_plus') {
    return this.request<{ url: string }>('/api/subscription/checkout', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ tier }),
    });
  }

  async getCustomerPortal() {
    return this.request<{ url: string }>('/api/subscription/portal', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Gamification endpoints
  async getGamificationStatus() {
    return this.request<{
      xp: number;
      level: number;
      totalAsIsScans: number;
      streakDays: number;
      bonusWatchlistSlots: number;
      referralCount: number;
      badges: Array<{ badgeId: string; badgeName: string; earnedAt?: string }>;
      lastSyncedAt: string | null;
    }>('/api/gamification/status', { requireAuth: true });
  }

  async recordShare(platform: string) {
    return this.request<{ xpAwarded: number; totalXp: number }>('/api/gamification/share', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ platform }),
    });
  }

  // Referral endpoints
  async getReferralStats() {
    return this.request<{
      referralCode: string;
      totalReferrals: number;
      qualifiedReferrals: number;
      pendingReferrals: number;
      rewardsThisMonth: number;
      premiumDaysRemaining?: number;
      premiumExpiresAt?: string | null;
      referrals: Array<{
        id: string;
        status: 'pending' | 'qualified' | 'rewarded';
        createdAt: string;
        qualifiedAt?: string;
        rewardedAt?: string;
      }>;
    }>('/api/referrals/stats', { requireAuth: true });
  }

  async getReferralCode() {
    return this.request<{ referralCode: string }>('/api/referrals/code', { requireAuth: true });
  }

  async getReferralRequirements() {
    return this.request<{
      method: 'as_is_scans' | 'level_based' | 'subscription';
      storeHasAsIs: boolean;
      asIsScans?: {
        required: number;
        current: number;
        isComplete: boolean;
      };
      levelBased?: {
        requiredXp: number;
        requiredLevel: number;
        currentXp: number;
        currentLevel: number;
        isComplete: boolean;
      };
      subscription?: {
        description: string;
      };
    }>('/api/referrals/requirements', { requireAuth: true });
  }

  async applyReferralCode(code: string) {
    return this.request<{ success: boolean; message: string }>('/api/referrals/apply', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ code }),
    });
  }

  // Watchlist endpoints
  async getWatchlist() {
    return this.request<{
      items: Array<{
        id: string;
        productCode: string;
        name: string;
        imageUrl: string | null;
        currentPrice: number | null;
        originalPrice: number | null;
        availability: string | null;
        stores: string[];
        checklists: Array<{ id: string; name: string; color: string }>;
        addedAt: string;
      }>;
      limit: number;
      used: number;
    }>('/api/watchlist', { requireAuth: true });
  }

  async addToWatchlist(productCode: string, stores?: string[]) {
    return this.request<{ id: string; message: string }>('/api/watchlist', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ productCode, stores }),
    });
  }

  async removeFromWatchlist(productCode: string) {
    return this.request<{ message: string }>(`/api/watchlist/${productCode}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async updateWatchlistItem(productCode: string, data: { stores?: string[]; notifyPrice?: boolean; notifyStock?: boolean }) {
    return this.request<{ message: string }>(`/api/watchlist/${productCode}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  // Checklist endpoints
  async getChecklists() {
    return this.request<{
      checklists: Array<{
        id: string;
        name: string;
        description: string | null;
        itemCount: number;
        completedCount: number;
        totalPrice: number;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('/api/checklists', { requireAuth: true });
  }

  async getChecklist(id: string) {
    return this.request<{
      checklist: {
        id: string;
        name: string;
        description: string | null;
        items: Array<{
          id: string;
          productCode: string;
          name: string;
          imageUrl: string | null;
          quantityNeeded: number;
          quantityPurchased: number;
          estimatedPrice: number | null;
          actualPrice: number | null;
          status: 'pending' | 'purchased' | 'installed';
          onWatchlist: boolean;
          notes: string | null;
          addedAt: string;
        }>;
        createdAt: string;
        updatedAt: string;
      };
    }>(`/api/checklists/${id}`, { requireAuth: true });
  }

  async createChecklist(name: string, description?: string) {
    return this.request<{ id: string; message: string }>('/api/checklists', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ name, description }),
    });
  }

  async updateChecklist(id: string, data: { name?: string; description?: string }) {
    return this.request<{ message: string }>(`/api/checklists/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteChecklist(id: string) {
    return this.request<{ message: string }>(`/api/checklists/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async addChecklistItem(checklistId: string, item: { productCode: string; quantityNeeded?: number; estimatedPrice?: number; notes?: string }) {
    return this.request<{ id: string; message: string }>(`/api/checklists/${checklistId}/items`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(item),
    });
  }

  async updateChecklistItem(checklistId: string, itemId: string, data: { quantityNeeded?: number; quantityPurchased?: number; actualPrice?: number; status?: string; notes?: string }) {
    return this.request<{ message: string }>(`/api/checklists/${checklistId}/items/${itemId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteChecklistItem(checklistId: string, itemId: string) {
    return this.request<{ message: string }>(`/api/checklists/${checklistId}/items/${itemId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async toggleChecklistItemWatchlist(checklistId: string, itemId: string) {
    return this.request<{ onWatchlist: boolean; message: string }>(`/api/checklists/${checklistId}/items/${itemId}/watchlist`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async addAllChecklistToWatchlist(checklistId: string) {
    return this.request<{ added: number; message: string }>(`/api/checklists/${checklistId}/add-all-to-watchlist`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async importChecklistItems(checklistId: string, items: Array<{ productCode: string; quantityNeeded?: number; estimatedPrice?: number }>) {
    return this.request<{ imported: number; message: string }>(`/api/checklists/${checklistId}/import`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ items }),
    });
  }

  // Notification preferences endpoints
  async getNotificationPreferences() {
    return this.request<{
      preferences: {
        emailPriceDrops: boolean;
        emailStockAlerts: boolean;
        emailNewDeals: boolean;
        emailDigestFrequency: 'immediate' | 'daily' | 'weekly' | 'never';
        pushEnabled: boolean;
      };
    }>('/api/user/notifications', { requireAuth: true });
  }

  async updateNotificationPreferences(prefs: {
    emailPriceDrops?: boolean;
    emailStockAlerts?: boolean;
    emailNewDeals?: boolean;
    emailDigestFrequency?: 'immediate' | 'daily' | 'weekly' | 'never';
    pushEnabled?: boolean;
  }) {
    return this.request<{ message: string }>('/api/user/notifications', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(prefs),
    });
  }

  // Website CMS endpoints
  async getWebsiteContent(section?: string, language?: string) {
    const params = new URLSearchParams();
    if (section) params.set('section', section);
    if (language) params.set('lang', language);
    const query = params.toString();
    const endpoint = query ? `/api/website/content?${query}` : '/api/website/content';
    return this.request<{
      content: Record<string, string>;
    }>(endpoint);
  }

  async getWebsiteTheme() {
    return this.request<{
      theme: Record<string, string>;
    }>('/api/website/theme');
  }

  async getWebsiteImages() {
    return this.request<{
      images: Array<{
        key: string;
        url: string;
        alt_text: string;
      }>;
    }>('/api/website/images');
  }

  // Get localized strings for website
  async getStrings(language: string, category?: string) {
    const params = new URLSearchParams({ lang: language });
    if (category) params.append('category', category);
    return this.request<Record<string, string>>(`/api/strings?${params.toString()}`);
  }

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================

  // Admin Metrics
  async getAdminOverview() {
    return this.request<{
      users: {
        total: number;
        active30d: number;
        newToday: number;
        newThisWeek: number;
        newThisMonth: number;
      };
      tiers: {
        free: number;
        hunter: number;
        pro: number;
        dealer: number;
      };
      products: {
        total: number;
        asIs: number;
        clearance: number;
        watched: number;
      };
      contributions: {
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      system: {
        apiStatus: string;
        lastSeedRun: string | null;
        seedSuccessRate: number;
      };
    }>('/api/admin/metrics/overview', { requireAuth: true });
  }

  async getAdminUserGrowth(period: string = '30d') {
    return this.request<{
      daily: Array<{ date: string; signups: number; cumulative: number }>;
    }>(`/api/admin/users/growth?period=${period}`, { requireAuth: true });
  }

  async getAdminTopWatched(limit: number = 20) {
    return this.request<
      Array<{
        productCode: string;
        name: string;
        imageUrl: string | null;
        watcherCount: number;
      }>
    >(`/api/admin/products/top-watched?limit=${limit}`, { requireAuth: true });
  }

  async getAdminContributionStats(period: string = '7d') {
    return this.request<{
      daily: Array<{
        date: string;
        count: number;
        byStore: Record<string, number>;
      }>;
      topStores: Array<{ storeId: string; count: number }>;
      byType: { asis: number; clearance: number };
      total: number;
    }>(`/api/admin/contributions/stats?period=${period}`, { requireAuth: true });
  }

  // Admin Users
  async getAdminUsers(params: {
    page?: number;
    limit?: number;
    tier?: string;
    search?: string;
    sort?: string;
    order?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.tier) query.set('tier', params.tier);
    if (params.search) query.set('search', params.search);
    if (params.sort) query.set('sort', params.sort);
    if (params.order) query.set('order', params.order);

    return this.request<{
      users: Array<{
        id: string;
        email: string;
        created_at: string;
        last_login: string | null;
        tier: string;
        is_admin: boolean;
        role?: string;
        auth_provider?: string;
        country?: string;
        total_scans?: number;
        verified_scans?: number;
        referral_code?: string;
        total_referrals?: number;
        successful_referrals?: number;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/users?${query.toString()}`, { requireAuth: true });
  }

  // Get comprehensive user statistics for analytics
  async getAdminUserStats(params: { country?: string } = {}) {
    const query = new URLSearchParams();
    if (params.country) query.set('country', params.country);

    return this.request<{
      total_users: number;
      new_signups: { week: number; month: number };
      active_users: { dau: number; wau: number; mau: number };
      tier_distribution: Record<string, number>;
      auth_provider_distribution: Record<string, number>;
      country_distribution: Record<string, number>;
      growth_data: Array<{ date: string; signups: number; active: number }>;
      retention: { day7: number; day30: number; day90: number };
    }>(`/api/admin/users/stats?${query.toString()}`, { requireAuth: true });
  }

  // Get detailed user info
  async getAdminUserDetail(userId: string) {
    return this.request<{
      user: {
        id: string;
        email: string;
        created_at: string;
        last_login: string | null;
        is_admin: boolean;
        role?: string;
        auth_provider?: string;
        country?: string;
        referral_code?: string;
        total_referrals?: number;
        successful_referrals?: number;
        extension_installed?: boolean;
        extension_installed_at?: string;
      };
      subscription: {
        tier: string;
        status: string;
        source?: string;
        started_at?: string;
        expires_at?: string;
      } | null;
      tier: string;
      watchlist: Array<{
        product_code: string;
        store_ids: string[];
        created_at: string;
      }>;
      scan_stats: {
        total_scans: number;
        verified_scans: number;
        unique_products: number;
        unique_stores: number;
        scans_this_month: number;
        scans_this_week: number;
        favorite_store_id?: string;
      };
      recent_scans: Array<{
        id: string;
        product_code: string;
        store_id: string;
        scan_type: string;
        scanned_at: string;
        location_verified: boolean;
        source: string;
      }>;
      referral: {
        code: string;
        total_referrals: number;
        successful_referrals: number;
        referred_by: {
          referrer_id: string;
          referrer_email: string;
          code_used: string;
          date: string;
        } | null;
        referrals_made: Array<{
          referee_id: string;
          status: string;
          created_at: string;
          qualified_at: string | null;
        }>;
      };
      gamification: {
        xp: number;
        level: number;
        total_as_is_scans: number;
        streak_days: number;
        last_active_date: string | null;
        bonus_watchlist_slots: number;
        synced_at: string | null;
        badges: Array<{
          badge_id: string;
          badge_name: string;
          earned_at: string;
        }>;
      };
      support_tickets: Array<{
        id: string;
        subject: string;
        status: string;
        category: string;
        priority: string;
        created_at: string;
        updated_at: string;
      }>;
      internal_notes: Array<{
        id: string;
        note: string;
        created_by: string;
        created_at: string;
      }>;
    }>(`/api/admin/users/${userId}`, { requireAuth: true });
  }

  async addAdminUserNote(userId: string, note: string) {
    return this.request<{
      note: {
        id: string;
        note: string;
        created_by: string;
        created_at: string;
      };
    }>(`/api/admin/users/${userId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
      requireAuth: true,
    });
  }

  async deleteAdminUserNote(userId: string, noteId: string) {
    return this.request<{ success: boolean }>(`/api/admin/users/${userId}/notes/${noteId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Admin Roles & Permissions
  async getAdminRoles() {
    return this.request<{
      roles: Array<{
        id: string;
        display_name: string;
        description: string;
        permissions: string[];
        is_system: boolean;
      }>;
    }>('/api/admin/roles', { requireAuth: true });
  }

  async getMyPermissions() {
    return this.request<{
      role: string;
      roleDisplay: { label: string; description: string };
      permissions: string[];
      rolePermissions: string[];
      overrides: Array<{
        permission: string;
        granted: boolean;
        expires_at: string | null;
      }>;
    }>('/api/admin/me/permissions', { requireAuth: true });
  }

  async getUserPermissions(userId: string) {
    return this.request<{
      role: string;
      rolePermissions: string[];
      areaAccess: Record<string, 'none' | 'read' | 'edit'>;
      usingAreaAccess: boolean;
      overrides: Array<{
        permission: string;
        granted: boolean;
        expires_at: string | null;
        reason?: string;
        created_at: string;
      }>;
      effectivePermissions: string[];
      auditLog: Array<{
        id: string;
        action: string;
        previous_value: string | null;
        new_value: string | null;
        reason: string | null;
        created_at: string;
      }>;
    }>(`/api/admin/users/${userId}/permissions`, { requireAuth: true });
  }

  async updateUserRole(userId: string, role: string, reason?: string) {
    return this.request<{ success: boolean; role: string }>(
      `/api/admin/users/${userId}/role`,
      {
        method: 'PUT',
        body: JSON.stringify({ role, reason }),
        requireAuth: true,
      }
    );
  }

  async setUserPermissionOverride(
    userId: string,
    permission: string,
    granted: boolean,
    reason?: string,
    expiresAt?: string
  ) {
    return this.request<{
      success: boolean;
      permission: string;
      granted: boolean;
    }>(`/api/admin/users/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission, granted, reason, expiresAt }),
      requireAuth: true,
    });
  }

  async removeUserPermissionOverride(userId: string, permission: string, reason?: string) {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return this.request<{ success: boolean; permission: string }>(
      `/api/admin/users/${userId}/permissions/${encodeURIComponent(permission)}${query}`,
      {
        method: 'DELETE',
        requireAuth: true,
      }
    );
  }

  async setUserAreaAccess(
    userId: string,
    areaAccess: Record<string, 'none' | 'read' | 'edit'>,
    reason?: string
  ) {
    return this.request<{ success: boolean; areaAccess: Record<string, string> }>(
      `/api/admin/users/${userId}/area-access`,
      {
        method: 'PUT',
        body: JSON.stringify({ areaAccess, reason }),
        requireAuth: true,
      }
    );
  }

  async setUserTier(userId: string, tier: 'free' | 'pro' | 'pro+') {
    return this.request<{ success: boolean; tier: string; message: string }>(
      `/api/admin/users/${userId}/tier`,
      {
        method: 'PUT',
        body: JSON.stringify({ tier }),
        requireAuth: true,
      }
    );
  }

  // Admin Products
  async getAdminProducts(params: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.type) query.set('type', params.type);
    if (params.search) query.set('search', params.search);

    return this.request<{
      products: Array<{
        product_code: string;
        name: string;
        current_price: number | null;
        original_price: number | null;
        is_as_is: boolean;
        is_clearance: boolean;
        image_url: string | null;
        updated_at: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/products?${query.toString()}`, { requireAuth: true });
  }

  // Admin System
  async getAdminSystemHealth() {
    return this.request<{
      api: {
        status: string;
        uptime: string;
        responseTime: number;
      };
      database: {
        status: string;
        latency: number;
      };
      seeds: {
        lastRun: string | null;
        successRate: number;
        runsThisWeek: number;
        successful: number;
        failed: number;
        running: number;
        storesCovered: number;
      };
    }>('/api/admin/system/health', { requireAuth: true });
  }

  async getAdminSeedRuns(limit: number = 50) {
    return this.request<
      Array<{
        id: number;
        run_type: string;
        seed_id: number | null;
        store_id: string | null;
        started_at: string;
        completed_at: string | null;
        status: string;
        products_found: number;
        products_contributed: number;
        error_message: string | null;
      }>
    >(`/api/admin/system/seed-runs?limit=${limit}`, { requireAuth: true });
  }

  async getAdminErrorLogs(period: string = '24h', limit: number = 100) {
    return this.request<
      Array<{
        id: number;
        timestamp: string;
        error_type: string;
        message: string;
        context: Record<string, unknown>;
        stack_trace: string | null;
        request_path: string | null;
        request_method: string | null;
      }>
    >(`/api/admin/system/errors?period=${period}&limit=${limit}`, { requireAuth: true });
  }

  // Admin Content Strings
  async getAdminStrings(params: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.category) query.set('category', params.category);
    if (params.search) query.set('search', params.search);

    return this.request<{
      strings: Array<{
        id: string;
        key: string;
        english_text: string;
        category: string | null;
        description: string | null;
        max_length: number | null;
        placeholders: Record<string, string> | null;
        created_at: string;
        updated_at: string;
        translation_count: number;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/strings?${query.toString()}`, { requireAuth: true });
  }

  async createAdminString(data: {
    key: string;
    english_text: string;
    category?: string | null;
    description?: string | null;
    max_length?: number | null;
    placeholders?: Record<string, string> | null;
  }) {
    return this.request<{ id: string }>('/api/admin/strings', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateAdminString(
    id: string,
    data: {
      key?: string;
      english_text?: string;
      category?: string | null;
      description?: string | null;
      max_length?: number | null;
      placeholders?: Record<string, string> | null;
    }
  ) {
    return this.request<{ success: boolean }>(`/api/admin/strings/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteAdminString(id: string) {
    return this.request<{ success: boolean }>(`/api/admin/strings/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Admin Translations
  async getAdminTranslations(params: {
    page?: number;
    limit?: number;
    language?: string;
    reviewed?: boolean;
    stringId?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.language) query.set('language', params.language);
    if (params.reviewed !== undefined) query.set('reviewed', params.reviewed.toString());
    if (params.stringId) query.set('stringId', params.stringId);

    return this.request<{
      translations: Array<{
        id: string;
        string_id: string | null;
        string_key: string | null;
        source_hash: string | null;
        source_text: string | null;
        language_code: string;
        translated_text: string;
        ai_model_used: string | null;
        confidence_score: number | null;
        human_reviewed: boolean;
        reviewed_by: string | null;
        reviewed_at: string | null;
        translated_at: string;
        created_at: string;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/translations?${query.toString()}`, { requireAuth: true });
  }

  async getAdminTranslationStats() {
    return this.request<{
      total_strings: number;
      translations_by_language: Record<string, number>;
      human_reviewed_count: number;
      pending_review_count: number;
    }>('/api/admin/translations/stats', { requireAuth: true });
  }

  async translateAdminString(stringId: string, language: string) {
    return this.request<{ translation: string }>('/api/admin/translations/translate', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ stringId, language }),
    });
  }

  async translateAllAdminStrings(language: string) {
    return this.request<{ translated: number; failed: number }>('/api/admin/translations/translate-all', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ language }),
    });
  }

  async markAdminTranslationReviewed(translationId: string) {
    return this.request<{ success: boolean }>(`/api/admin/translations/${translationId}/review`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // App String Translation Dashboard (similar to Website Content)
  async getAppStringTranslationStatus() {
    return this.request<{
      strings: Array<{
        id: string;
        key: string;
        english_text: string;
        category: string | null;
        translations: Record<string, { status: string; translated_text?: string }>;
      }>;
      languages: string[];
    }>('/api/admin/strings/translation-status', { requireAuth: true });
  }

  async getAppStringsToTranslate(force: boolean = false) {
    return this.request<{
      items: Array<{
        id: string;
        key: string;
        english_text: string;
        category: string | null;
      }>;
      already_complete?: number;
    }>(`/api/admin/strings/items-to-translate?force=${force}`, { requireAuth: true });
  }

  async translateAppStringToAllLanguages(stringId: string, force: boolean = false) {
    return this.request<{
      success: boolean;
      key: string;
      saved: number;
      failed: number;
      languages_translated: number;
    }>(`/api/admin/strings/${stringId}/translate-all-languages`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ force }),
    });
  }

  async syncSharedStringsFromWebsite() {
    return this.request<{
      success: boolean;
      synced: number;
      message: string;
    }>(`/api/admin/strings/sync-from-website`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async syncSharedStringsBidirectional() {
    return this.request<{
      success: boolean;
      message: string;
      details: {
        websiteToApp: number;
        appToWebsite: number;
        skipped: Array<{ key: string; reason: string }>;
        errors: string[];
      };
    }>(`/api/admin/strings/sync-shared-bidirectional`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Reset all app string translations (delete all content_translations)
  async resetAppStringTranslations() {
    return this.request<{
      success: boolean;
      message: string;
      deleted_count: number;
    }>('/api/admin/translations/reset-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Support/KB/FAQ methods removed — now handled by AXISS widget

  // Admin Settings
  async getAdminMaintenanceMode() {
    return this.request<{ enabled: boolean }>('/api/settings/maintenance-mode');
  }

  async setAdminMaintenanceMode(enabled: boolean) {
    return this.request<{ success: boolean; enabled: boolean }>('/api/settings/admin/maintenance-mode', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ enabled }),
    });
  }

  // Admin Website CMS
  async getAdminWebsiteContent() {
    return this.request<{
      content: Record<string, string>;
    }>('/api/website/content', { requireAuth: true });
  }

  async getAdminWebsiteTheme() {
    return this.request<{
      theme: Record<string, string>;
    }>('/api/website/theme', { requireAuth: true });
  }

  async getAdminWebsiteImages() {
    return this.request<{
      images: Array<{
        key: string;
        url: string;
        alt_text: string;
      }>;
    }>('/api/website/images', { requireAuth: true });
  }

  async updateAdminWebsiteContentBulk(data: {
    updates: Array<{ section: string; key: string; value: string }>;
  }) {
    return this.request<{ success: boolean }>('/api/website/content/bulk', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateAdminWebsiteThemeBulk(data: { theme: Record<string, string> }) {
    return this.request<{ success: boolean }>('/api/website/theme/bulk', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateAdminWebsiteImage(data: { key: string; url: string; alt_text: string }) {
    return this.request<{ success: boolean }>('/api/website/images', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // ADMIN WEBSITE TRANSLATION DASHBOARD ENDPOINTS
  // ============================================

  // Get translation status grid
  async getAdminTranslationStatus() {
    return this.request<{
      content: Array<{
        content_id: string;
        section: string;
        key: string;
        english_value: string;
        translations: Record<string, { status: string; quality_score: number | null }>;
      }>;
      languages: string[];
    }>('/api/website/translations/status', { requireAuth: true });
  }

  // Get single translation with details
  async getAdminTranslation(contentId: string, lang: string) {
    return this.request<{
      content: {
        id: string;
        section: string;
        key: string;
        english_value: string;
      };
      translation: {
        id: string;
        content_id: string;
        source_hash: string;
        language_code: string;
        translated_text: string;
        ai_model_used: string | null;
        quality_score: number | null;
        quality_issues: Array<{ type: string; description: string }> | null;
        status: string;
        reviewed_by: string | null;
        reviewed_at: string | null;
        created_at: string;
        updated_at: string;
      } | null;
      language: string;
    }>(`/api/website/translations/${contentId}/${lang}`, { requireAuth: true });
  }

  // Update translation manually
  async updateAdminTranslation(contentId: string, lang: string, translatedText: string, reviewedBy?: string) {
    return this.request<{ success: boolean }>(`/api/website/translations/${contentId}/${lang}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ translated_text: translatedText, reviewed_by: reviewedBy }),
    });
  }

  // Analyze translation quality with AI
  async analyzeAdminTranslation(contentId: string, lang: string) {
    return this.request<{
      content_id: string;
      language: string;
      english_value: string;
      translated_text: string;
      analysis: {
        score: number;
        issues: Array<{ type: string; description: string }>;
        suggestion: string | null;
      };
    }>(`/api/website/translations/analyze/${contentId}/${lang}`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // AI chat for translation questions
  async chatAboutTranslation(question: string, context?: {
    language?: string;
    english_text?: string;
    translated_text?: string;
  }) {
    return this.request<{
      answer: string;
      model: string;
    }>('/api/website/translations/chat', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ question, context }),
    });
  }

  // Trigger retranslation of specific content
  async retranslateAdminContent(contentId: string, languages?: string[]) {
    return this.request<{
      success: boolean;
      message: string;
      content_id: string;
      languages: string[];
    }>(`/api/website/translations/retranslate/${contentId}`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ languages }),
    });
  }

  // Trigger translation for all website content (background job - may timeout)
  async translateAllAdminContent() {
    return this.request<{
      success: boolean;
      message: string;
      job_id: string;
      total_items: number;
      total_languages: number;
      languages: string[];
    }>('/api/website/translations/translate-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Get list of items to translate (for frontend-driven translation)
  // Use force=true to get ALL items regardless of existing translations
  async getItemsToTranslate(force: boolean = false) {
    const query = force ? '?force=true' : '';
    return this.request<{
      items: Array<{
        id: string;
        section: string;
        key: string;
        english_value: string;
      }>;
      total: number;
      already_complete?: number;
      force?: boolean;
    }>(`/api/website/translations/items-to-translate${query}`, {
      requireAuth: true,
    });
  }

  // Translate a single item synchronously (frontend-driven)
  // Use force=true to skip cache checks and always translate
  async translateSingleItem(contentId: string, force: boolean = false) {
    const query = force ? '?force=true' : '';
    return this.request<{
      success: boolean;
      content_id: string;
      item: string;
      duration_ms: number;
      skipped?: boolean;
      languages_translated?: number;
      force?: boolean;
    }>(`/api/website/translations/translate-item/${contentId}${query}`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Get translation job status by ID
  async getTranslationJobStatus(jobId: string) {
    return this.request<{
      id: string;
      status: 'running' | 'completed' | 'failed' | 'cancelled';
      total_items: number;
      completed_items: number;
      failed_items: number;
      total_languages: number;
      progress: number;
      started_at: string;
      completed_at: string | null;
      error_message: string | null;
      current_item: string | null;
      elapsed_ms: number;
      estimated_remaining_ms: number;
      item_started_at: string | null;
      last_error: string | null;
      fatal_error: string | null;
    }>(`/api/website/translations/job/${jobId}`, { requireAuth: true });
  }

  // Get latest/active translation job
  async getLatestTranslationJob() {
    return this.request<{
      job: {
        id: string;
        status: 'running' | 'completed' | 'failed' | 'cancelled';
        total_items: number;
        completed_items: number;
        failed_items: number;
        total_languages: number;
        progress: number;
        started_at: string;
        completed_at: string | null;
        current_item: string | null;
        elapsed_ms: number;
        estimated_remaining_ms: number;
      } | null;
    }>('/api/website/translations/job', { requireAuth: true });
  }

  // Cancel a running translation job
  async cancelTranslationJob(jobId: string) {
    return this.request<{
      success: boolean;
      message: string;
      job_id: string;
    }>(`/api/website/translations/job/${jobId}/cancel`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Force cancel ALL running translation jobs
  async cancelAllTranslationJobs() {
    return this.request<{
      success: boolean;
      message: string;
      cancelled_count: number;
    }>('/api/website/translations/jobs/cancel-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Reset all translations (delete all)
  async resetAllTranslations() {
    return this.request<{
      success: boolean;
      message: string;
      deleted_count: number;
    }>('/api/website/translations/reset-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // ============================================
  // ADMIN SEED MANAGEMENT ENDPOINTS
  // ============================================

  // Seed CRUD
  async getAdminSeeds(params: {
    page?: number;
    limit?: number;
    country?: string;
    status?: string;
    search?: string;
    sort?: string;
    sortDirection?: 'asc' | 'desc';
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.country) query.set('country', params.country);
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    if (params.sort) query.set('sort', params.sort);
    if (params.sortDirection) query.set('sortDirection', params.sortDirection);

    return this.request<{
      seeds: Array<{
        id: number;
        name: string;
        country: string;
        timezone: string;
        status: 'active' | 'paused' | 'inactive';
        center_latitude: number | null;
        center_longitude: number | null;
        radius_miles: number;
        auto_assign_enabled: boolean;
        geo_config: Record<string, unknown>;
        last_run_at: string | null;
        last_run_status: string | null;
        created_at: string;
        updated_at: string;
        store_count: number;
        running_progress: {
          total_stores: number;
          stores_completed: number;
          current_action: string | null;
        } | null;
        next_run_at: string | null;
        is_queued: boolean;
        current_store_index: number;
        last_stats: {
          asis: { products: number; stores: number; percentage: number };
          last_chance: { products: number; stores: number; percentage: number };
          family_offers: { products: number; stores: number; percentage: number };
          limited_time: { products: number; stores: number; percentage: number };
          locations: number;
        } | null;
        error_count_24h: number;
        schedules: Array<{
          id: number;
          run_type: string;
          cron_expression: string;
          enabled: boolean;
        }>;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/seeds?${query.toString()}`, { requireAuth: true });
  }

  async getAdminSeed(seedId: number) {
    return this.request<{
      id: number;
      name: string;
      country: string;
      timezone: string;
      status: string;
      center_latitude: number | null;
      center_longitude: number | null;
      radius_miles: number;
      auto_assign_enabled: boolean;
      geo_config: Record<string, unknown>;
      last_run_at: string | null;
      last_run_status: string | null;
      created_at: string;
      updated_at: string;
      assigned_stores: Array<{
        id: string;
        name: string;
        city: string | null;
        state: string | null;
        country: string;
        latitude: number | null;
        longitude: number | null;
      }>;
      schedules: Array<{
        id: number;
        run_type: string;
        cron_expression: string;
        timezone: string;
        enabled: boolean;
        next_run_at: string | null;
      }>;
      store_count: number;
      recent_runs: Array<{
        id: number;
        run_type: string | null;
        status: string;
        products_found: number | null;
        started_at: string;
        completed_at: string | null;
        error_message: string | null;
      }>;
      last_stats?: {
        asis: number;
        last_chance: number;
        family_offers: number;
        limited_time: number;
        locations: number;
      };
    }>(`/api/admin/seeds/${seedId}`, { requireAuth: true });
  }

  async getAdminSeedReferrals(seedId: number) {
    return this.request<{
      seed_id: number;
      seed_name: string;
      seed_country?: string;
      has_user_account: boolean;
      user_id?: string;
      referral_code?: string;
      referral_link?: string;
      total_referrals: number;
      successful_referrals: number;
      conversion_rate: number;
      referrals_by_period?: {
        last_24h: number;
        last_7d: number;
        last_30d: number;
      };
      recent_referrals?: Array<{
        id: string;
        referred_user_id: string;
        conversion_type: string | null;
        converted_at: string | null;
        created_at: string;
      }>;
      message?: string;
    }>(`/api/admin/seeds/${seedId}/referrals`, { requireAuth: true });
  }

  async getAdminSeedStats() {
    return this.request<{
      total_seeds: number;
      active_seeds: number;
      paused_seeds: number;
      total_stores: number;
      covered_stores: number;
      uncovered_stores: number;
      runs_today: number;
      runs_this_week: number;
      success_rate: number;
    }>('/api/admin/seeds/stats', { requireAuth: true });
  }

  async getAdminSeedMapData() {
    return this.request<{
      seeds: Array<{
        id: number;
        name: string;
        country: string;
        status: string;
        center_latitude: number;
        center_longitude: number;
        radius_miles: number;
        store_count: number;
      }>;
      stores: Array<{
        id: string;
        name: string;
        city: string;
        state: string;
        country: string;
        latitude: number;
        longitude: number;
        is_active: boolean;
        assigned_seed_id: number | null;
      }>;
      unassigned_store_ids: string[];
    }>('/api/admin/seeds/map', { requireAuth: true });
  }

  async createAdminSeed(data: {
    name: string;
    country: 'US' | 'CA';
    timezone: string;
    center_latitude?: number;
    center_longitude?: number;
    radius_miles?: number;
    auto_assign_enabled?: boolean;
    geo_config?: Record<string, unknown>;
    store_ids?: string[];
    schedules?: Array<{
      run_type: 'asis' | 'clearance';
      cron_expression: string;
      timezone?: string;
      enabled?: boolean;
    }>;
  }) {
    return this.request<{ seed: { id: number; name: string } }>('/api/admin/seeds', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateAdminSeed(
    seedId: number,
    data: {
      name?: string;
      country?: 'US' | 'CA';
      timezone?: string;
      status?: 'active' | 'paused' | 'inactive';
      center_latitude?: number;
      center_longitude?: number;
      radius_miles?: number;
      auto_assign_enabled?: boolean;
      geo_config?: Record<string, unknown>;
    }
  ) {
    return this.request<{ success: boolean }>(`/api/admin/seeds/${seedId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteAdminSeed(seedId: number) {
    return this.request<{ success: boolean }>(`/api/admin/seeds/${seedId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Seed Controls
  async pauseAdminSeed(seedId: number) {
    return this.request<{ success: boolean; status: string }>(`/api/admin/seeds/${seedId}/pause`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async resumeAdminSeed(seedId: number) {
    return this.request<{ success: boolean; status: string }>(`/api/admin/seeds/${seedId}/resume`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async triggerAdminSeedRun(seedId: number, runType?: 'asis' | 'clearance') {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/seeds/${seedId}/run`,
      {
        method: 'POST',
        requireAuth: true,
        body: runType ? JSON.stringify({ run_type: runType }) : undefined,
      }
    );
  }

  async forceStopAdminSeed(seedId: number) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/seeds/${seedId}/force-stop`,
      {
        method: 'POST',
        requireAuth: true,
      }
    );
  }

  async triggerAllAdminSeeds() {
    return this.request<{
      success: boolean;
      message: string;
      seeds_triggered: number;
      seed_names: string[];
    }>('/api/admin/seeds/run-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async getAdminSeedRunHistory(seedId: number, limit: number = 50) {
    return this.request<
      Array<{
        id: number;
        run_type: string | null;
        status: string;
        products_found: number | null;
        started_at: string;
        completed_at: string | null;
        error_message: string | null;
      }>
    >(`/api/admin/seeds/${seedId}/runs?limit=${limit}`, { requireAuth: true });
  }

  // Seed Store Assignments
  async updateAdminSeedStores(seedId: number, storeIds: string[]) {
    return this.request<{ success: boolean; count: number }>(`/api/admin/seeds/${seedId}/stores`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ store_ids: storeIds }),
    });
  }

  async updateAdminSeedCoverage(
    seedId: number,
    data: {
      center_latitude: number;
      center_longitude: number;
      radius_miles: number;
    }
  ) {
    return this.request<{ success: boolean }>(`/api/admin/seeds/${seedId}/coverage`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async autoAssignAdminSeeds() {
    return this.request<{
      success: boolean;
      assigned_count: number;
      unassigned_count: number;
      unassigned_store_ids: string[];
    }>('/api/admin/seeds/auto-assign', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Seed Health
  async getAdminSeedHealth(seedId: number) {
    return this.request<{
      id: number;
      seed_id: number;
      report_date: string;
      total_products: number;
      products_updated: number;
      products_stale: number;
      products_missing_price: number;
      products_missing_image: number;
      stores_with_errors: string[];
      error_summary: Record<string, unknown>;
      health_score: number;
      created_at: string;
    }>(`/api/admin/seeds/${seedId}/health`, { requireAuth: true });
  }

  async runAdminSeedHealthCheck(seedId: number) {
    return this.request<{
      seed_id: number;
      seed_name: string;
      report_date: string;
      total_products: number;
      products_updated_24h: number;
      products_updated_48h: number;
      products_stale: number;
      products_missing_price: number;
      products_missing_image: number;
      products_missing_name: number;
      stores_checked: number;
      stores_with_data: number;
      stores_with_errors: string[];
      error_summary: Record<string, number>;
      health_score: number;
      issues: Array<{
        severity: 'critical' | 'warning' | 'info';
        category: string;
        message: string;
        affected_count?: number;
        store_ids?: string[];
      }>;
    }>(`/api/admin/seeds/${seedId}/health/check`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async runAdminAllSeedHealthChecks() {
    return this.request<{
      success: boolean;
      seeds_checked: number;
      results: Array<{
        seed_id: number;
        seed_name: string;
        health_score: number;
        issues_count: number;
      }>;
    }>('/api/admin/seeds/health/check-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async getAdminSeedHealthReportUrl(seedId: number): Promise<string> {
    const accessToken = await this.getToken();
    return `${this.baseUrl}/api/admin/seeds/${seedId}/health/report?token=${accessToken}`;
  }

  // ============================================
  // ADMIN SEED ERROR AND TIMING ENDPOINTS
  // ============================================

  async getAdminSeedErrors(seedId: number, hours: number = 24) {
    return this.request<{
      seed_id: number;
      hours: number;
      total_errors: number;
      summary: Array<{
        error_type: string;
        count: number;
        latest_message: string;
        affected_stores: string[];
      }>;
      errors: Array<{
        id: number;
        correlation_id: string;
        seed_id: number;
        store_id: string;
        run_type: string;
        error_name: string;
        error_message: string;
        error_stack?: string;
        error_phase: string;
        error_operation: string;
        page_url?: string;
        page_title?: string;
        created_at: string;
      }>;
    }>(`/api/admin/seeds/${seedId}/errors?hours=${hours}`, { requireAuth: true });
  }

  async getAdminSeedTiming(seedId: number, days: number = 7) {
    return this.request<{
      seed_id: number;
      days: number;
      stats: {
        total_runs: number;
        by_run_type: Record<string, {
          count: number;
          avg_duration_ms: number;
          avg_browser_launch_ms: number;
          avg_navigation_ms: number;
          avg_parsing_ms: number;
          avg_contribution_ms: number;
          min_duration_ms: number;
          max_duration_ms: number;
        }>;
        by_store: Record<string, {
          count: number;
          avg_duration_ms: number;
        }>;
        slowest_runs: Array<{
          store_id: string;
          run_type: string;
          duration_ms: number;
          completed_at: string;
        }>;
      };
    }>(`/api/admin/seeds/${seedId}/timing?days=${days}`, { requireAuth: true });
  }

  async getAdminSeedTimingTrends(seedId: number, days: number = 7) {
    return this.request<{
      seed_id: number;
      days: number;
      trends: Array<{
        date: string;
        runs: number;
        avg_duration_ms: number;
        by_run_type: Record<string, {
          count: number;
          avg_duration_ms: number;
        }>;
      }>;
    }>(`/api/admin/seeds/${seedId}/timing/trends?days=${days}`, { requireAuth: true });
  }

  async getAdminRecentErrors(hours: number = 24, limit: number = 100) {
    return this.request<{
      hours: number;
      total_errors: number;
      errors: Array<{
        id: number;
        correlation_id: string;
        seed_id: number;
        seed_name: string;
        seed_country: string;
        store_id: string;
        error_phase: string;
        error_name: string;
        error_message: string;
        created_at: string;
      }>;
      by_seed: Array<{
        seed_id: number;
        seed_name: string;
        error_count: number;
        stores_with_errors: number;
      }>;
    }>(`/api/admin/seeds/errors/recent?hours=${hours}&limit=${limit}`, { requireAuth: true });
  }

  async getAdminSeedRunLogs(seedId: number, runId: number) {
    return this.request<{
      run: {
        id: number;
        seed_ref_id: number;
        store_id: string;
        run_type: string;
        status: string;
        products_found: number;
        error_message?: string;
        correlation_id?: string;
        timing_data?: Record<string, unknown>;
        duration_ms?: number;
        started_at: string;
        completed_at: string;
      };
      logs: Array<{
        id: number;
        correlation_id: string;
        log_level: string;
        message: string;
        context: Record<string, unknown>;
        data?: Record<string, unknown>;
        timing_operation?: string;
        timing_duration_ms?: number;
        created_at: string;
      }>;
      errors: Array<{
        id: number;
        error_name: string;
        error_message: string;
        error_stack?: string;
        error_phase: string;
        error_operation: string;
        page_url?: string;
        created_at: string;
      }>;
    }>(`/api/admin/seeds/${seedId}/runs/${runId}/logs`, { requireAuth: true });
  }

  // ============================================
  // ADMIN SCHEDULE MANAGEMENT ENDPOINTS
  // ============================================

  async getAdminSeedSchedules(seedId: number) {
    return this.request<Array<{
      id: number;
      seed_id: number;
      run_type: string;
      cron_expression: string;
      timezone: string;
      enabled: boolean;
      next_run_at: string | null;
      started_at: string | null;
      locked_by: string | null;
      stores_completed: number | null;
      total_stores: number | null;
      consecutive_failures: number;
      last_error_message: string | null;
    }>>(`/api/admin/seeds/${seedId}/schedules`, { requireAuth: true });
  }

  async updateAdminSeedSchedule(
    seedId: number,
    scheduleId: number,
    data: { cron_expression?: string; enabled?: boolean; timezone?: string }
  ) {
    return this.request<{ success: boolean }>(`/api/admin/seeds/${seedId}/schedules/${scheduleId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async createAdminSeedSchedule(
    seedId: number,
    data: { run_type: string; cron_expression: string; timezone?: string; enabled?: boolean }
  ) {
    return this.request<{
      id: number;
      seed_id: number;
      run_type: string;
      cron_expression: string;
      timezone: string;
      enabled: boolean;
    }>(`/api/admin/seeds/${seedId}/schedules`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteAdminSeedSchedule(seedId: number, scheduleId: number) {
    return this.request<{ success: boolean }>(`/api/admin/seeds/${seedId}/schedules/${scheduleId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // ============================================
  // ADMIN WATCHDOG ENDPOINTS
  // ============================================

  async getAdminStuckSchedules() {
    return this.request<{
      stuck_schedules: Array<{
        schedule_id: number;
        seed_id: number;
        seed_name: string;
        run_type: string;
        started_at: string;
        locked_by: string | null;
        stores_completed: number;
        total_stores: number;
        consecutive_failures: number;
        last_error_message: string | null;
        duration_minutes: number;
      }>;
      count: number;
    }>('/api/admin/seeds/schedules/stuck', { requireAuth: true });
  }

  async triggerAdminWatchdog() {
    return this.request<{
      success: boolean;
      source: string;
      recovered: number;
      paused: number;
      expired_locks_cleaned: number;
    }>('/api/admin/seeds/watchdog', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async recoverAdminSchedule(scheduleId: number) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/seeds/schedules/${scheduleId}/recover`,
      {
        method: 'POST',
        requireAuth: true,
      }
    );
  }

  async getAdminScheduleStatus() {
    return this.request<{
      summary: {
        total: number;
        enabled: number;
        disabled: number;
        running: number;
        stuck: number;
        due: number;
        healthy: number;
        with_errors: number;
      };
      schedules: Array<{
        id: number;
        seed_id: number;
        seed_name: string;
        seed_status: string;
        run_type: string;
        enabled: boolean;
        next_run_at: string | null;
        started_at: string | null;
        locked_by: string | null;
        stores_completed: number | null;
        total_stores: number | null;
        consecutive_failures: number;
        last_error: string | null;
        is_stuck: boolean;
        is_due: boolean;
      }>;
    }>('/api/admin/seeds/schedules/status', { requireAuth: true });
  }

  // ============================================
  // ADMIN OPERATIONS CENTER ENDPOINTS
  // ============================================

  async getOpsLiveStatus() {
    return this.request<{
      seeds: {
        running: number;
        queued: number;
        successRate24h: number;
        errorsLast1h: number;
        errorsLast24h: number;
      };
      system: {
        apiStatus: 'healthy' | 'degraded' | 'down';
        apiLatencyMs: number;
        dbLatencyMs: number;
      };
      data: {
        productsUpdated24h: number;
        staleProducts: number;
        storesCovered: number;
        totalStores: number;
      };
      alerts: {
        critical: number;
        warning: number;
        info: number;
      };
      lastUpdated: string;
    }>('/api/admin/ops/live-status', { requireAuth: true });
  }

  async getOpsRunningSeeds() {
    return this.request<{
      seeds: Array<{
        seedId: number;
        seedName: string;
        scheduleId: number;
        runType: string;
        storeId: string | null;
        storeName: string | null;
        correlationId: string | null;
        progress: number;
        storesCompleted: number;
        totalStores: number;
        currentPhase: string | null;
        startedAt: string;
        durationMinutes: number;
        lockedBy: string | null;
      }>;
    }>('/api/admin/ops/running-seeds', { requireAuth: true });
  }

  async getOpsAlerts() {
    return this.request<{
      alerts: Array<{
        id: string;
        severity: 'critical' | 'warning' | 'info';
        title: string;
        message: string;
        source: string;
        sourceId?: string | number;
        timestamp: string;
        actionUrl?: string;
      }>;
    }>('/api/admin/ops/alerts', { requireAuth: true });
  }

  async getOpsActivityFeed(limit: number = 50) {
    return this.request<{
      activities: Array<{
        id: string;
        type: 'seed_start' | 'seed_complete' | 'seed_error' | 'contribution' | 'user_action';
        title: string;
        details: string;
        timestamp: string;
        correlationId?: string;
        seedId?: number;
        storeId?: string;
      }>;
    }>(`/api/admin/ops/activity-feed?limit=${limit}`, { requireAuth: true });
  }

  async triggerOpsWatchdog() {
    return this.request<{
      success: boolean;
      recovered: number;
      paused: number;
      expiredLocksCleaned: number;
    }>('/api/admin/ops/watchdog', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async pauseAllSeeds() {
    return this.request<{
      success: boolean;
      pausedCount: number;
    }>('/api/admin/ops/pause-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async resumeAllSeeds() {
    return this.request<{
      success: boolean;
      resumedCount: number;
    }>('/api/admin/ops/resume-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  async clearStuckSeeds() {
    return this.request<{
      success: boolean;
      clearedCount: number;
    }>('/api/admin/ops/clear-stuck', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // ============================================
  // ADMIN ERROR INVESTIGATION ENDPOINTS
  // ============================================

  async getErrorTimeline(params?: {
    seedId?: number;
    storeId?: string;
    phase?: string;
    hours?: number;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.seedId) query.set('seedId', params.seedId.toString());
    if (params?.storeId) query.set('storeId', params.storeId);
    if (params?.phase) query.set('phase', params.phase);
    if (params?.hours) query.set('hours', params.hours.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    return this.request<{
      errors: Array<{
        id: number;
        correlationId: string;
        seedId: number | null;
        seedName: string | null;
        storeId: string | null;
        runType: string | null;
        errorPhase: string | null;
        errorName: string | null;
        errorMessage: string | null;
        errorStack: string | null;
        pageUrl: string | null;
        pageTitle: string | null;
        pageState: Record<string, unknown> | null;
        retryCount: number;
        isRecoverable: boolean;
        createdAt: string;
      }>;
      total: number;
      offset: number;
      limit: number;
    }>(`/api/admin/errors/timeline?${query.toString()}`, { requireAuth: true });
  }

  async getErrorTrace(correlationId: string) {
    return this.request<{
      correlationId: string;
      seedRun: {
        id: number;
        seedId: number;
        seedName: string | null;
        storeId: string;
        runType: string;
        status: string;
        productsFound: number | null;
        startedAt: string;
        completedAt: string | null;
        durationMs: number | null;
        errorMessage: string | null;
        errorDetails: Record<string, unknown> | null;
        timingData: Record<string, unknown> | null;
      } | null;
      logs: Array<{
        id: number;
        logLevel: string;
        message: string;
        context: Record<string, unknown> | null;
        data: Record<string, unknown> | null;
        timingOperation: string | null;
        timingDurationMs: number | null;
        createdAt: string;
      }>;
      errors: Array<{
        id: number;
        errorPhase: string | null;
        errorName: string | null;
        errorMessage: string | null;
        errorStack: string | null;
        pageUrl: string | null;
        pageState: Record<string, unknown> | null;
        createdAt: string;
      }>;
    }>(`/api/admin/errors/trace/${correlationId}`, { requireAuth: true });
  }

  async getErrorPatterns(hours: number = 24) {
    return this.request<{
      patterns: Array<{
        errorName: string;
        errorPhase: string | null;
        count: number;
        affectedSeeds: number[];
        affectedStores: string[];
        firstSeen: string;
        lastSeen: string;
        trend: 'increasing' | 'decreasing' | 'stable';
        latestMessage: string | null;
      }>;
    }>(`/api/admin/errors/patterns?hours=${hours}`, { requireAuth: true });
  }

  async getErrorPhases() {
    return this.request<{
      phases: string[];
    }>('/api/admin/errors/phases', { requireAuth: true });
  }

  async getErrorSummary(hours: number = 24) {
    return this.request<{
      total: number;
      byPhase: Record<string, number>;
      hours: number;
    }>(`/api/admin/errors/summary?hours=${hours}`, { requireAuth: true });
  }

  async retryFailedSeed(seedId: number, storeId?: string, runType?: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>(`/api/admin/errors/${seedId}/retry`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ storeId, runType }),
    });
  }

  // ============================================
  // USER INSIGHTS ENDPOINTS
  // ============================================

  async getInsightsGamification() {
    return this.request<{
      leaderboard: Array<{
        rank: number;
        email: string;
        xp: number;
        level: number;
        totalScans: number;
        streakDays: number;
        joinedAt: string;
      }>;
      levelDistribution: Array<{ level: number; count: number }>;
      badgeDistribution: Array<{ name: string; count: number }>;
      streakDistribution: Array<{ range: string; count: number }>;
      summary: {
        totalXp: number;
        avgXp: number;
        usersWithXp: number;
        totalBadgesEarned: number;
        usersWithStreaks: number;
      };
    }>('/api/admin/insights/gamification', { requireAuth: true });
  }

  async getInsightsEngagement() {
    return this.request<{
      metrics: {
        dau: number;
        dauYesterday: number;
        wau: number;
        mau: number;
        totalUsers: number;
        dauMauRatio: number;
        contributorRate: number;
        uniqueContributors: number;
      };
      dailyContributions: Array<{ date: string; count: number }>;
      topContributors: Array<{ userId: string; email: string; count: number }>;
      usersByCountry: Array<{ country: string; count: number }>;
    }>('/api/admin/insights/engagement', { requireAuth: true });
  }

  async getInsightsAuditLog(params?: { limit?: number; offset?: number; action?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.action) query.set('action', params.action);

    return this.request<{
      logs: Array<{
        id: string;
        userId: string;
        userEmail: string;
        action: string;
        changedBy: string;
        adminEmail: string;
        oldValue: unknown;
        newValue: unknown;
        reason: string | null;
        createdAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/api/admin/insights/audit-log?${query}`, { requireAuth: true });
  }

  async getInsightsGamificationEvents(limit: number = 50) {
    return this.request<{
      events: Array<{
        id: string;
        userId: string;
        userEmail: string;
        eventType: string;
        xpAmount: number;
        source: string;
        metadata: Record<string, unknown>;
        createdAt: string;
      }>;
    }>(`/api/admin/insights/gamification-events?limit=${limit}`, { requireAuth: true });
  }

  // ============================================
  // ADMIN STORE MANAGEMENT ENDPOINTS
  // ============================================

  async getAdminStores(params?: {
    country?: string;
    assigned?: boolean;
    seed_id?: number;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.country) query.set('country', params.country);
    if (params?.assigned !== undefined) query.set('assigned', params.assigned.toString());
    if (params?.seed_id) query.set('seed_id', params.seed_id.toString());
    if (params?.search) query.set('search', params.search);

    return this.request<{
      stores: Array<{
        id: string;
        name: string;
        slug: string | null;
        city: string | null;
        state: string | null;
        country: string;
        timezone: string | null;
        region: string | null;
        latitude: number | null;
        longitude: number | null;
        as_is_url: string | null;
        is_active: boolean;
        created_at: string;
        updated_at: string;
        assigned_seed_id: number | null;
      }>;
      total: number;
    }>(`/api/admin/stores?${query.toString()}`, { requireAuth: true });
  }

  async getAdminUnassignedStores() {
    return this.request<{
      stores: Array<{
        id: string;
        name: string;
        city: string | null;
        state: string | null;
        country: string;
        latitude: number | null;
        longitude: number | null;
      }>;
      total: number;
    }>('/api/admin/stores/unassigned', { requireAuth: true });
  }

  async getAdminStore(storeId: string) {
    return this.request<{
      id: string;
      name: string;
      city: string | null;
      state: string | null;
      country: string;
      latitude: number | null;
      longitude: number | null;
      is_active: boolean;
      assigned_seed_id: number | null;
      assignment_priority: number | null;
      auto_assigned: boolean;
    }>(`/api/admin/stores/${storeId}`, { requireAuth: true });
  }

  async updateAdminStore(storeId: string, data: Partial<{
    name: string;
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    is_active: boolean;
  }>) {
    return this.request<{ success: boolean }>(`/api/admin/stores/${storeId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteAdminStore(storeId: string) {
    return this.request<{ success: boolean }>(`/api/admin/stores/${storeId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Store Sync
  async getAdminStoreSyncStatus() {
    return this.request<{
      recent_syncs: Array<{
        id: number;
        country: string;
        stores_found: number;
        stores_added: string[];
        stores_removed: string[];
        sync_status: string;
        error_message: string | null;
        synced_at: string;
      }>;
      last_sync_by_country: Record<string, {
        id: number;
        country: string;
        stores_found: number;
        synced_at: string;
      }>;
    }>('/api/admin/stores/sync/status', { requireAuth: true });
  }

  async triggerAdminStoreSync(country: string = 'US') {
    return this.request<{
      success: boolean;
      country: string;
      stores_found: number;
      stores_added: number;
      stores_removed: number;
      new_store_ids: string[];
      removed_store_ids: string[];
      sync_log_id: number;
    }>(`/api/admin/stores/sync?country=${country}`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async getAdminStoreSyncHistory(limit: number = 20) {
    return this.request<
      Array<{
        id: number;
        country: string;
        stores_found: number;
        stores_added: string[];
        stores_removed: string[];
        sync_status: string;
        error_message: string | null;
        synced_at: string;
      }>
    >(`/api/admin/stores/sync/history?limit=${limit}`, { requireAuth: true });
  }

  // ============================================
  // ADMIN STORE DATA BROWSER ENDPOINTS
  // ============================================

  async getAdminStoreBrowserOverview() {
    return this.request<{
      countries: Array<{
        code: string;
        name: string;
        store_count: number;
        product_count: number;
        last_chance_count: number;
        family_offers_count: number;
        limited_time_count: number;
        physical_count: number;
        health_score: number;
        health_status: 'healthy' | 'warning' | 'error';
        last_updated: string | null;
        metrics: {
          completeness: number;
          freshness_24h: number;
          freshness_7d: number;
        };
      }>;
      total_stores: number;
      total_products: number;
      global_health_score: number;
    }>('/api/admin/store-browser/overview', { requireAuth: true });
  }

  async getAdminCountryStores(
    country: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      sort?: string;
      sortDirection?: 'asc' | 'desc';
    }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.search) query.set('search', params.search);
    if (params?.sort) query.set('sort', params.sort);
    if (params?.sortDirection) query.set('sortDirection', params.sortDirection);

    return this.request<{
      country: {
        code: string;
        name: string;
      };
      stores: Array<{
        id: string;
        name: string;
        slug: string | null;
        slug_verification_status: 'verified' | 'failed' | 'pending' | 'never_checked';
        slug_verified_at: string | null;
        city: string | null;
        state: string | null;
        address_verification_status: string | null;
        as_is_url: string | null;
        product_count: number;
        last_chance_count: number;
        family_offers_count: number;
        limited_time_count: number;
        physical_count: number;
        last_seed_run: {
          date: string | null;
          status: string | null;
          products_found: number | null;
        };
        health: {
          score: number;
          status: 'healthy' | 'warning' | 'error';
          freshness_24h: number;
          freshness_7d: number;
          completeness: number;
          stale_count: number;
        };
        assigned_seed_id: number | null;
        assigned_seed_name: string | null;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/store-browser/${country}/stores?${query.toString()}`, { requireAuth: true });
  }

  async getAdminStoreBrowserSummary(storeId: string) {
    return this.request<{
      store: {
        id: string;
        name: string;
        city: string | null;
        state: string | null;
        country: string;
        latitude: number | null;
        longitude: number | null;
        as_is_url: string | null;
        slug: string | null;
        slug_verification_status?: string;
        slug_verified_at?: string | null;
        has_as_is?: boolean;
        last_as_is_check?: string | null;
        as_is_detection_method?: 'manual' | 'seed_browser' | null;
        street_address?: string | null;
        postal_code?: string | null;
        phone_number?: string | null;
        address_last_updated?: string | null;
        address_verification_status?: string;
        address_submission_count?: number;
        address_verified_at?: string | null;
        address_verification_method?: 'crowdsourced' | 'admin' | 'legacy' | null;
        proposed_address?: {
          street_address: string | null;
          city: string | null;
          postal_code: string | null;
          phone_number: string | null;
          unique_submitter_count: number | null;
          geocoding_status: 'passed' | 'failed_distance' | 'failed_geocoding' | null;
          geocoding_distance_km: number | null;
          last_submission_at: string | null;
        } | null;
      };
      asis: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      last_chance: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      family_offers: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      limited_time: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      physical: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      last_seed_run: {
        date: string | null;
        status: string | null;
        products_found: number | null;
      };
      health_score: number;
      health_status: 'healthy' | 'warning' | 'error';
    }>(`/api/admin/store-browser/${storeId}/summary`, { requireAuth: true });
  }

  async getAdminStoreAsIsProducts(
    storeId: string,
    params?: {
      page?: number;
      limit?: number;
      filter?: 'all' | 'issues';
      search?: string;
    }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.filter) query.set('filter', params.filter);
    if (params?.search) query.set('search', params.search);

    return this.request<{
      store: {
        id: string;
        name: string;
        country: string;
      };
      summary: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      products: Array<{
        product_code: string;
        name: string | null;
        current_price: number | null;
        original_price: number | null;
        image_url: string | null;
        availability: string | null;
        last_checked: string;
        is_as_is: boolean;
        is_clearance: boolean;
        issues: Array<'missing_price' | 'missing_image' | 'missing_name' | 'stale'>;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/store-browser/${storeId}/products?${query.toString()}`, { requireAuth: true });
  }

  async getAdminStoreClearanceProducts(
    storeId: string,
    params?: {
      page?: number;
      limit?: number;
      filter?: 'all' | 'issues';
      search?: string;
    }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.filter) query.set('filter', params.filter);
    if (params?.search) query.set('search', params.search);

    return this.request<{
      store: {
        id: string;
        name: string;
        country: string;
      };
      summary: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      products: Array<{
        product_code: string;
        name: string | null;
        price: number | null;
        original_price: number | null;
        image_url: string | null;
        availability: string | null;
        last_checked: string;
        first_seen: string | null;
        issues: Array<'missing_price' | 'missing_image' | 'missing_name' | 'stale'>;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/store-browser/${storeId}/clearance?${query.toString()}`, { requireAuth: true });
  }

  // Generic sale products method for all sale types (last_chance, family_offers, limited_time)
  async getAdminStoreSaleProducts(
    storeId: string,
    saleType: 'last_chance' | 'family_offers' | 'limited_time',
    params?: {
      page?: number;
      limit?: number;
      filter?: 'all' | 'issues';
      search?: string;
    }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.filter) query.set('filter', params.filter);
    if (params?.search) query.set('search', params.search);

    // Map sale type to API endpoint
    const endpointMap: Record<string, string> = {
      last_chance: 'last-chance',
      family_offers: 'family-offers',
      limited_time: 'limited-time',
    };
    const endpoint = endpointMap[saleType] || 'last-chance';

    return this.request<{
      store: {
        id: string;
        name: string;
        country: string;
      };
      summary: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      products: Array<{
        product_code: string;
        name: string | null;
        price: number | null;
        original_price: number | null;
        image_url: string | null;
        availability: string | null;
        last_checked: string;
        first_seen: string | null;
        issues: Array<'missing_price' | 'missing_image' | 'missing_name' | 'stale'>;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/store-browser/${storeId}/${endpoint}?${query.toString()}`, { requireAuth: true });
  }

  async getAdminStorePhysicalProducts(
    storeId: string,
    params?: {
      page?: number;
      limit?: number;
      filter?: 'all' | 'issues';
      search?: string;
    }
  ) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', params.page.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.filter) query.set('filter', params.filter);
    if (params?.search) query.set('search', params.search);

    return this.request<{
      store: {
        id: string;
        name: string;
        country: string;
      };
      summary: {
        total_products: number;
        missing_price: number;
        missing_image: number;
        missing_name: number;
        stale_products: number;
        freshness_24h: number;
        freshness_7d: number;
        last_updated: string | null;
      };
      products: Array<{
        product_code: string;
        name: string | null;
        image_url: string | null;
        last_seen: string;
        confidence: number | null;
        scan_count: number;
        issues: Array<'missing_price' | 'missing_image' | 'missing_name' | 'stale'>;
      }>;
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/store-browser/${storeId}/physical?${query.toString()}`, { requireAuth: true });
  }

  async getAdminStoreBrowserHealth(storeId: string) {
    return this.request<{
      store_id: string;
      store_name: string;
      report_date: string;
      asis: {
        completeness: {
          with_price: { count: number; percentage: number };
          with_image: { count: number; percentage: number };
          with_name: { count: number; percentage: number };
          fully_complete: { count: number; percentage: number };
        };
        freshness: {
          updated_24h: { count: number; percentage: number };
          updated_7d: { count: number; percentage: number };
          updated_30d: { count: number; percentage: number };
          stale: { count: number; percentage: number };
        };
        accuracy: {
          seed_products_found: number;
          actual_product_count: number;
          match_rate: number;
          last_seed_run: string | null;
          last_seed_status: string | null;
        };
      };
      clearance: {
        completeness: {
          with_price: { count: number; percentage: number };
          with_image: { count: number; percentage: number };
          with_name: { count: number; percentage: number };
          fully_complete: { count: number; percentage: number };
        };
        freshness: {
          updated_24h: { count: number; percentage: number };
          updated_7d: { count: number; percentage: number };
          updated_30d: { count: number; percentage: number };
          stale: { count: number; percentage: number };
        };
      };
      physical: {
        freshness: {
          updated_24h: { count: number; percentage: number };
          updated_7d: { count: number; percentage: number };
          updated_30d: { count: number; percentage: number };
          stale: { count: number; percentage: number };
        };
        scan_stats: {
          total_scans: number;
          unique_products: number;
          avg_confidence: number;
        };
      };
      recent_runs: Array<{
        id: number;
        run_type: string;
        status: string;
        products_found: number;
        started_at: string;
        completed_at: string | null;
      }>;
      health_score: number;
      health_status: 'healthy' | 'warning' | 'error';
      issues: Array<{
        severity: 'critical' | 'warning' | 'info';
        category: string;
        message: string;
        affected_count?: number;
      }>;
    }>(`/api/admin/store-browser/${storeId}/health`, { requireAuth: true });
  }

  // Store trends
  async getAdminStoreTrends(storeId: string, days: number = 30) {
    return this.request<{
      store_id: string;
      store_name: string;
      period_days: number;
      trend: {
        dates: string[];
        health_scores: number[];
        completeness_scores: number[];
        freshness_scores: number[];
        locale_scores: number[];
      };
      analysis: {
        direction: 'improving' | 'declining' | 'stable';
        change_percentage: number;
        current_score: number;
        average_score: number;
        min_score: number;
        max_score: number;
        anomalies: Array<{ date: string; score: number; deviation: number }>;
      };
    }>(`/api/admin/store-browser/${storeId}/trends?days=${days}`, { requireAuth: true });
  }

  // Country trends
  async getAdminCountryTrends(country: string, days: number = 30) {
    return this.request<{
      country_code: string;
      country_name: string;
      period_days: number;
      trend: {
        dates: string[];
        health_scores: number[];
        store_counts: Array<{ healthy: number; warning: number; error: number }>;
      };
      analysis: {
        direction: 'improving' | 'declining' | 'stable';
        change_percentage: number;
        current_score: number;
        average_score: number;
        min_score: number;
        max_score: number;
        anomalies: Array<{ date: string; score: number; deviation: number }>;
      };
    }>(`/api/admin/store-browser/${country}/trends?days=${days}`, { requireAuth: true });
  }

  // Store locale health
  async getAdminStoreLocaleHealth(storeId: string) {
    return this.request<{
      store_id: string;
      store_name: string;
      country: string;
      locale_config: {
        expected_language: string;
        expected_currency: string;
        currency_symbol: string;
        locale: string;
      } | null;
      health: {
        total_products_checked: number;
        language_score: number;
        currency_score: number;
        overall_score: number;
        correct_language: number;
        correct_currency: number;
      };
      issues: Array<{
        product_code: string;
        issue_type: 'language' | 'currency';
        expected: string;
        detected: string;
        sample_text: string;
      }>;
    }>(`/api/admin/store-browser/${storeId}/locale-health`, { requireAuth: true });
  }

  // Health alerts
  async getAdminAlerts(options?: { severity?: string; entity_type?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.severity) params.set('severity', options.severity);
    if (options?.entity_type) params.set('entity_type', options.entity_type);
    if (options?.limit) params.set('limit', options.limit.toString());

    return this.request<{
      alerts: Array<{
        id: number;
        alert_type: string;
        severity: 'critical' | 'warning' | 'info';
        entity_type: string;
        entity_id: string;
        title: string;
        description: string | null;
        details: Record<string, unknown>;
        is_resolved: boolean;
        resolved_at: string | null;
        resolved_by: string | null;
        created_at: string;
      }>;
      summary: {
        total_unresolved: number;
        critical_count: number;
        warning_count: number;
        info_count: number;
        by_type: Record<string, number>;
      };
    }>(`/api/admin/store-browser/alerts?${params.toString()}`, { requireAuth: true });
  }

  // Resolve alert
  async resolveAdminAlert(alertId: number) {
    return this.request<{ success: boolean }>(
      `/api/admin/store-browser/alerts/${alertId}/resolve`,
      { method: 'POST', requireAuth: true }
    );
  }

  // Store alerts
  async getAdminStoreAlerts(storeId: string, includeResolved: boolean = false) {
    return this.request<{
      alerts: Array<{
        id: number;
        alert_type: string;
        severity: 'critical' | 'warning' | 'info';
        entity_type: string;
        entity_id: string;
        title: string;
        description: string | null;
        details: Record<string, unknown>;
        is_resolved: boolean;
        resolved_at: string | null;
        resolved_by: string | null;
        created_at: string;
      }>;
    }>(`/api/admin/store-browser/${storeId}/alerts?include_resolved=${includeResolved}`, { requireAuth: true });
  }

  // Update store slug
  async updateStoreSlug(storeId: string, slug: string | null) {
    return this.request<{
      success: boolean;
      store: {
        id: string;
        name: string;
        slug: string | null;
        slug_verification_status?: string;
        slug_verified_at?: string | null;
      };
      verification?: {
        storeId: string;
        slug: string;
        status: 'verified' | 'failed' | 'pending' | 'never_checked';
        verifiedAt: string;
        error?: string;
      };
    }>(`/api/admin/store-browser/${storeId}/slug`, {
      method: 'PATCH',
      requireAuth: true,
      body: JSON.stringify({ slug }),
    });
  }

  // Verify store slug
  async verifyStoreSlug(storeId: string) {
    return this.request<{
      success: boolean;
      store: {
        id: string;
        name: string;
        slug: string | null;
        slug_verification_status: string;
        slug_verified_at: string | null;
      };
      verification: {
        storeId: string;
        slug: string;
        status: 'verified' | 'failed' | 'pending' | 'never_checked';
        verifiedAt: string;
        error?: string;
      };
    }>(`/api/admin/store-browser/${storeId}/verify-slug`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Update store As-Is department status
  async updateStoreAsIs(storeId: string, hasAsIs: boolean) {
    return this.request<{
      success: boolean;
      store: {
        id: string;
        name: string;
        has_as_is: boolean;
        last_as_is_check: string;
        as_is_detection_method: string;
      };
    }>(`/api/admin/stores/${storeId}/as-is`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ has_as_is: hasAsIs }),
    });
  }

  async updateStore(storeId: string, updates: {
    name?: string;
    city?: string;
    state?: string;
    street_address?: string;
    postal_code?: string;
    phone_number?: string;
    latitude?: number;
    longitude?: number;
    slug?: string;
    timezone?: string;
    as_is_url?: string;
    raw_address?: string; // Pasted IKEA address format - will parse and geocode
  }) {
    return this.request<{
      success: boolean;
      store: {
        id: string;
        name: string;
        city: string | null;
        state: string | null;
        street_address: string | null;
        postal_code: string | null;
        phone_number: string | null;
        latitude: number | null;
        longitude: number | null;
        as_is_url: string | null;
        country: string;
      };
    }>(`/api/admin/stores/${storeId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(updates),
    });
  }

  async createStore(store: {
    name: string;
    country: string;
    city?: string;
    state?: string;
    street_address?: string;
    postal_code?: string;
    phone_number?: string;
    latitude?: number;
    longitude?: number;
    slug?: string;
    timezone?: string;
  }) {
    return this.request<{
      success: boolean;
      store: {
        id: string;
        name: string;
        country: string;
        city: string | null;
        state: string | null;
        slug: string | null;
      };
    }>('/api/admin/stores', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(store),
    });
  }

  async deleteStore(storeId: string) {
    return this.request<{ success: boolean }>(`/api/admin/stores/${storeId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Store closure reports
  async getClosureReports() {
    return this.request<{
      closureReports: Array<{
        store: { id: string; name: string; city?: string; state?: string; country: string };
        count: number;
        reports: Array<{
          id: number;
          store_id: string;
          user_id?: string;
          is_seed: boolean;
          reason?: string;
          status: string;
          created_at: string;
        }>;
      }>;
      totalStores: number;
      totalReports: number;
    }>('/api/admin/stores/closure-reports', { requireAuth: true });
  }

  async approveStoreClosure(storeId: string, notes?: string) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/stores/${storeId}/approve-closure`,
      {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ notes }),
      }
    );
  }

  async rejectStoreClosure(storeId: string, notes?: string) {
    return this.request<{ success: boolean; message: string }>(
      `/api/admin/stores/${storeId}/reject-closure`,
      {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ notes }),
      }
    );
  }

  async getAddressVerificationStatus(storeId: string) {
    return this.request<{
      store: {
        id: string;
        name: string;
        street_address?: string;
        postal_code?: string;
        phone_number?: string;
        address_verification_status?: string;
        address_submission_count?: number;
        address_last_updated?: string;
      };
      verification?: {
        verified_street_address?: string;
        verified_city?: string;
        verified_postal_code?: string;
        verified_phone_number?: string;
        verification_status: string;
        submission_count: number;
        unique_submitter_count: number;
        verified_at?: string;
      };
      recentContributions: Array<{
        id: number;
        street_address?: string;
        city?: string;
        postal_code?: string;
        phone_number?: string;
        is_seed: boolean;
        created_at: string;
      }>;
    }>(`/api/admin/stores/${storeId}/address-status`, { requireAuth: true });
  }

  async updateAddressVerification(
    storeId: string,
    action: 'approve' | 'reject' | 'verify_manual' | 'unverify',
    notes?: string
  ) {
    return this.request<{
      success: boolean;
      message: string;
      action: string;
    }>(`/api/admin/stores/${storeId}/address-verification`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ action, notes }),
    });
  }

  // Get count of stores with slugs to verify
  async getSlugsToVerify() {
    return this.request<{
      total: number;
      byCountry: Record<string, number>;
    }>('/api/admin/store-browser/slugs-to-verify', { requireAuth: true });
  }

  // Verify all slugs
  async verifyAllSlugs(country?: string) {
    const params = country ? `?country=${country}` : '';
    return this.request<{
      success: boolean;
      verified: number;
      failed: number;
      skipped: number;
      total: number;
      results: Array<{
        storeId: string;
        slug: string;
        status: 'verified' | 'failed' | 'pending' | 'never_checked';
        verifiedAt: string;
        error?: string;
      }>;
    }>(`/api/admin/store-browser/verify-all-slugs${params}`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // ============================================
  // AI SETTINGS ENDPOINTS
  // ============================================

  // Get all AI providers
  async getAIProviders() {
    return this.request<{
      providers: Array<{
        provider_name: string;
        display_name: string;
        docs_url: string;
        is_enabled: boolean;
        priority: number;
        rate_limit_per_minute: number | null;
        rate_limit_per_day: number | null;
        current_usage_minute: number;
        current_usage_day: number;
        api_key_status: 'configured' | 'from_env' | 'not_configured';
        api_key_masked: string | null;
        api_key_set_at: string | null;
        is_healthy: boolean;
        last_health_check: string | null;
        base_url: string | null;
        default_model: string | null;
        cost_per_1k_input_tokens: number;
        cost_per_1k_output_tokens: number;
      }>;
    }>('/api/admin/ai/providers', { requireAuth: true });
  }

  // Update AI provider config
  async updateAIProvider(name: string, updates: {
    is_enabled?: boolean;
    priority?: number;
    rate_limit_per_minute?: number | null;
    rate_limit_per_day?: number | null;
    base_url?: string | null;
    default_model?: string | null;
  }) {
    return this.request<{ success: boolean }>(`/api/admin/ai/providers/${name}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(updates),
    });
  }

  // Set AI provider API key
  async setAIProviderApiKey(name: string, apiKey: string) {
    return this.request<{ success: boolean; api_key_masked: string }>(
      `/api/admin/ai/providers/${name}/api-key`,
      {
        method: 'PUT',
        requireAuth: true,
        body: JSON.stringify({ api_key: apiKey }),
      }
    );
  }

  // Delete AI provider API key
  async deleteAIProviderApiKey(name: string) {
    return this.request<{ success: boolean }>(
      `/api/admin/ai/providers/${name}/api-key`,
      { method: 'DELETE', requireAuth: true }
    );
  }

  // Trigger health check for AI provider
  async triggerAIHealthCheck(name: string) {
    return this.request<{
      success: boolean;
      is_healthy: boolean;
      latency_ms?: number;
      error?: string;
    }>(`/api/admin/ai/providers/${name}/health-check`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // Get AI usage statistics
  async getAIUsageStats(params?: { start?: string; end?: string }) {
    const query = new URLSearchParams();
    if (params?.start) query.set('start', params.start);
    if (params?.end) query.set('end', params.end);
    const queryStr = query.toString();
    return this.request<{
      period: { start: string; end: string };
      by_provider: Record<string, {
        total_requests: number;
        successful_requests: number;
        failed_requests: number;
        total_tokens: number;
        prompt_tokens: number;
        completion_tokens: number;
        estimated_cost: number;
        avg_latency_ms: number;
      }>;
      by_use_case: Record<string, {
        total_requests: number;
        total_tokens: number;
        estimated_cost: number;
      }>;
      daily: Array<{ date: string; total_requests: number; total_tokens: number; estimated_cost: number }>;
      totals: {
        requests: number;
        tokens: number;
        cost: number;
        avg_latency_ms: number;
      };
    }>(queryStr ? `/api/admin/ai/usage?${queryStr}` : '/api/admin/ai/usage', { requireAuth: true });
  }

  // FAQ methods removed — now handled by AXISS widget

  // ==========================================================================
  // DEALS (Public sale products browser)
  // ==========================================================================

  /**
   * Get countries with deal products
   */
  async getDealsCountries(): Promise<{
    countries: Array<{
      code: string;
      name: string;
      count: number;
    }>;
  }> {
    return this.request('/api/deals/countries', { requireAuth: false });
  }

  /**
   * Get deals summary (category counts)
   */
  async getDealsSummary(country?: string, storeId?: string): Promise<{
    last_chance: { count: number };
    family_offers: { count: number };
    limited_time: { count: number };
    as_is: { count: number; requires_login: boolean };
  }> {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    if (storeId) params.set('store', storeId);
    const query = params.toString();
    return this.request(`/api/deals/summary${query ? `?${query}` : ''}`, { requireAuth: false });
  }

  /**
   * Get deal products with filters
   */
  async getDealsProducts(params: {
    category?: 'last_chance' | 'family_offers' | 'limited_time' | 'as_is' | 'all';
    country?: string;
    store_id?: string;
    search?: string;
    sort?: 'newest' | 'price_asc' | 'price_desc' | 'discount';
    cursor?: string;
    limit?: number;
  }): Promise<{
    products: Array<{
      product_code: string;
      name: string | null;
      image_url: string | null;
      price: number | null;
      original_price: number | null;
      discount_percent: number | null;
      category: 'last_chance' | 'family_offers' | 'limited_time' | 'as_is';
      store_id: string;
      store_name: string;
      store_city: string;
      country: string;
      first_seen: string | null;
      last_checked: string;
      as_is_condition?: string;
    }>;
    has_more: boolean;
    next_cursor: string | null;
    requires_login?: boolean;
    message?: string;
    tier_info?: {
      tier: string;
      delay_hours: number;
    };
    total_count?: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.set('category', params.category);
    if (params.country) searchParams.set('country', params.country);
    if (params.store_id) searchParams.set('store_id', params.store_id);
    if (params.search) searchParams.set('search', params.search);
    if (params.sort) searchParams.set('sort', params.sort);
    if (params.cursor) searchParams.set('cursor', params.cursor);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    // Include auth if available for tier-based features (As-Is delay)
    return this.request(`/api/deals/products${query ? `?${query}` : ''}`, { includeAuthIfAvailable: true });
  }

  /**
   * Detect user's country from Cloudflare
   */
  async detectCountry(): Promise<{ country: string }> {
    return this.request('/api/deals/detect-country', { requireAuth: false });
  }

  /**
   * Get all IKEA stores
   */
  async getStores(): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      city: string;
      state: string;
      region: 'midwest' | 'northeast' | 'southeast' | 'south' | 'west';
      country: 'US' | 'CA';
      latitude: number;
      longitude: number;
    }>;
  }> {
    return this.request('/api/stores', { requireAuth: false });
  }

  // ============================================
  // RUMIOS API ENDPOINTS
  // ============================================

  /**
   * Get user's Rumio balance
   */
  async getRumioBalance(): Promise<{
    success: boolean;
    data: {
      balance: number;
      tier_costs: { super: number; fresh: number; standard: number };
      admin_auto_refill?: { refilled: boolean; amount: number };
    };
  }> {
    return this.request('/api/rumios/balance', { requireAuth: true });
  }

  /**
   * Get available Rumio packs for purchase
   */
  async getRumioPacks(): Promise<{
    packs: Array<{
      type: 'super' | 'fresh' | 'standard';
      name: string;
      rumios: number;
      price: number;
      price_formatted: string;
      available: boolean;
      requires_tier?: string;
    }>;
    user_tier: string;
  }> {
    return this.request('/api/rumios/packs');
  }

  /**
   * Purchase Rumio pack (creates Stripe checkout session)
   */
  async purchaseRumios(pack_type: 'super' | 'fresh' | 'standard'): Promise<{
    checkout_url: string;
    session_id: string;
  }> {
    return this.request('/api/rumios/purchase', {
      method: 'POST',
      body: JSON.stringify({ pack_type }),
    });
  }

  /**
   * Unlock a mystery product with Rumios
   */
  async unlockProduct(params: {
    product_code: string;
    store_id: string;
    tier: 'super' | 'fresh' | 'standard';
  }): Promise<{
    success: boolean;
    new_balance: number;
    rumios_spent: number;
    xp_earned: number;
  }> {
    return this.request('/api/rumios/unlock', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get list of products user has unlocked
   */
  async getUnlockedProducts(): Promise<{
    unlocked: Array<{
      product_code: string;
      store_id: string;
      rumio_type: string;
      unlocked_at: string;
    }>;
  }> {
    return this.request('/api/rumios/unlocked');
  }

  /**
   * Get Rumio purchase history
   */
  async getRumioHistory(): Promise<{
    purchases: Array<{
      id: string;
      pack_type: string;
      rumios_added: number;
      amount_cents: number;
      created_at: string;
    }>;
  }> {
    return this.request('/api/rumios/history');
  }

  // ============================================
  // DAILY SPIN API ENDPOINTS
  // ============================================

  /**
   * Get daily spin status
   */
  async getSpinStatus(): Promise<{
    can_spin: boolean;
    last_spin_date: string | null;
    last_spin_result: {
      prize_type: 'nothing' | 'xp' | 'super' | 'fresh' | 'standard' | 'pro_pass';
      rumios_won: number;
      xp_won: number;
      multiplier: number;
    } | null;
    total_spins: number;
    total_rumios_won: number;
    multipliers: {
      has_extension: boolean;
      has_app: boolean;
      total_multiplier: number;
    };
  }> {
    return this.request('/api/spin/status');
  }

  /**
   * Execute daily spin
   */
  async executeSpin(): Promise<{
    prize_type: 'nothing' | 'xp' | 'super' | 'fresh' | 'standard' | 'pro_pass';
    rumios_won: number;
    xp_won: number;
    multiplier: number;
    base_rumios: number;
    new_balance: {
      super_rumios: number;
      fresh_rumios: number;
      standard_rumios: number;
    };
    new_xp: number;
    new_level: number;
  }> {
    return this.request('/api/spin/execute', { method: 'POST' });
  }

  /**
   * Get spin multipliers status
   */
  async getSpinMultipliers(): Promise<{
    has_extension: boolean;
    has_app: boolean;
    total_multiplier: number;
    breakdown: Array<{ source: string; multiplier: number; active: boolean }>;
  }> {
    return this.request('/api/spin/multipliers');
  }

  /**
   * Update multiplier status (for extension/app detection)
   */
  async updateSpinMultipliers(params: {
    has_extension?: boolean;
    has_app?: boolean;
  }): Promise<{
    has_extension: boolean;
    has_app: boolean;
    total_multiplier: number;
  }> {
    return this.request('/api/spin/update-multipliers', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /**
   * Get spin history
   */
  async getSpinHistory(limit?: number): Promise<{
    spins: Array<{
      spin_date: string;
      prize_type: string;
      rumios_won: number;
      xp_won: number;
      multiplier: number;
    }>;
  }> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/api/spin/history${query}`);
  }

  // Smart support methods removed — now handled by AXISS widget

  // ============================================
  // ADMIN AI AGENT ENDPOINTS
  // ============================================

  /**
   * Get AI agent rules
   */
  async getAIAgentRules() {
    return this.request<{
      rules: Array<{
        id: string;
        rule_type: 'behavior' | 'response' | 'restriction' | 'escalation';
        title: string;
        content: string;
        priority: number;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/rules', { requireAuth: true });
  }

  /**
   * Create AI agent rule
   */
  async createAIAgentRule(rule: {
    rule_type: string;
    title: string;
    content: string;
    priority?: number;
    is_active?: boolean;
  }) {
    return this.request<{
      rule: {
        id: string;
        rule_type: string;
        title: string;
        content: string;
        priority: number;
        is_active: boolean;
      };
    }>('/api/admin/ai-agent/rules', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(rule),
    });
  }

  /**
   * Update AI agent rule
   */
  async updateAIAgentRule(
    id: string,
    rule: Partial<{
      rule_type: string;
      title: string;
      content: string;
      priority: number;
      is_active: boolean;
    }>
  ) {
    return this.request<{ rule: { id: string } }>(`/api/admin/ai-agent/rules/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(rule),
    });
  }

  /**
   * Delete AI agent rule
   */
  async deleteAIAgentRule(id: string) {
    return this.request<{ success: boolean }>(`/api/admin/ai-agent/rules/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Get AI agent prompts
   */
  async getAIAgentPrompts() {
    return this.request<{
      prompts: Array<{
        id: string;
        name: string;
        content: string;
        version: number;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/prompts', { requireAuth: true });
  }

  /**
   * Update AI agent prompt
   */
  async updateAIAgentPrompt(name: string, data: { content: string; is_active?: boolean }) {
    return this.request<{
      prompt: {
        id: string;
        name: string;
        content: string;
        version: number;
        is_active: boolean;
      };
    }>(`/api/admin/ai-agent/prompts/${name}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Get AI agent templates
   */
  async getAIAgentTemplates() {
    return this.request<{
      templates: Array<{
        id: string;
        name: string;
        category: string;
        trigger_keywords: string[];
        content: string;
        variables: string[];
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/templates', { requireAuth: true });
  }

  /**
   * Create AI agent template
   */
  async createAIAgentTemplate(template: {
    name: string;
    category: string;
    trigger_keywords: string[];
    content: string;
    variables?: string[];
    is_active?: boolean;
  }) {
    return this.request<{ template: { id: string } }>('/api/admin/ai-agent/templates', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(template),
    });
  }

  /**
   * Update AI agent template
   */
  async updateAIAgentTemplate(
    id: string,
    template: Partial<{
      name: string;
      category: string;
      trigger_keywords: string[];
      content: string;
      variables: string[];
      is_active: boolean;
    }>
  ) {
    return this.request<{ template: { id: string } }>(`/api/admin/ai-agent/templates/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(template),
    });
  }

  /**
   * Delete AI agent template
   */
  async deleteAIAgentTemplate(id: string) {
    return this.request<{ success: boolean }>(`/api/admin/ai-agent/templates/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Get AI escalation rules
   */
  async getAIEscalationRules() {
    return this.request<{
      rules: Array<{
        id: string;
        name: string;
        rule_type: 'confidence' | 'keyword' | 'category' | 'sentiment';
        config: Record<string, unknown>;
        notify_email: string | null;
        priority: number;
        is_active: boolean;
        created_at: string;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/escalation-rules', { requireAuth: true });
  }

  /**
   * Create AI escalation rule
   */
  async createAIEscalationRule(rule: {
    name: string;
    rule_type: string;
    config: Record<string, unknown>;
    notify_email?: string;
    priority?: number;
    is_active?: boolean;
  }) {
    return this.request<{ rule: { id: string } }>('/api/admin/ai-agent/escalation-rules', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(rule),
    });
  }

  /**
   * Update AI escalation rule
   */
  async updateAIEscalationRule(
    id: string,
    rule: Partial<{
      name: string;
      rule_type: string;
      config: Record<string, unknown>;
      notify_email: string | null;
      priority: number;
      is_active: boolean;
    }>
  ) {
    return this.request<{ rule: { id: string } }>(`/api/admin/ai-agent/escalation-rules/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(rule),
    });
  }

  /**
   * Delete AI escalation rule
   */
  async deleteAIEscalationRule(id: string) {
    return this.request<{ success: boolean }>(`/api/admin/ai-agent/escalation-rules/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Get AI agent metrics summary
   */
  async getAIAgentMetricsSummary() {
    return this.request<{
      summary: {
        total_tickets: number;
        resolved_tickets: number;
        escalated_tickets: number;
        resolution_rate: number;
        escalation_rate: number;
        avg_confidence: number;
        avg_response_time_ms: number;
        satisfaction_positive: number;
        satisfaction_negative: number;
        satisfaction_rate: number;
        top_escalation_reasons: Array<{ reason: string; count: number }>;
      };
    }>('/api/admin/ai-agent/metrics/summary', { requireAuth: true });
  }

  /**
   * Test AI agent configuration with a message
   */
  async testAIAgent(data: { message: string; test_escalation?: boolean }) {
    return this.request<{
      system_prompt: string;
      system_prompt_length: number;
      matching_templates: Array<{
        name: string;
        category: string;
        matched_keywords: string[];
      }>;
      escalation_check: {
        shouldEscalate: boolean;
        shouldFlag: boolean;
        triggeredRules: string[];
        notifyEmails: string[];
        reason: string;
      } | null;
    }>('/api/admin/ai-agent/test', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // AI AGENT SETTINGS
  // ============================================

  /**
   * Get AI agent settings
   */
  async getAIAgentSettings() {
    return this.request<{
      settings: {
        writing_style?: {
          tone: 'formal' | 'friendly' | 'casual';
          conciseness: number;
          use_emojis: boolean;
          personalization: number;
        };
        response_timing?: {
          urgent_min_minutes: number;
          urgent_max_minutes: number;
          normal_min_hours: number;
          normal_max_hours: number;
          daytime_start: number;
          daytime_end: number;
        };
        agent_identity?: {
          title: string;
          show_avatar: boolean;
          default_avatar_id: string | null;
        };
      };
    }>('/api/admin/ai-agent/settings', { requireAuth: true });
  }

  /**
   * Update AI agent settings
   */
  async updateAIAgentSettings(settings: Record<string, unknown>) {
    return this.request<{ success: boolean }>('/api/admin/ai-agent/settings', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(settings),
    });
  }

  /**
   * Get AI agent names by language
   */
  async getAIAgentNames() {
    return this.request<{
      names: Array<{
        id: string;
        language_code: string;
        names: string[];
        is_active: boolean;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/settings/names', { requireAuth: true });
  }

  /**
   * Update AI agent names for a language
   */
  async updateAIAgentNames(language: string, data: { names: string[]; is_active?: boolean }) {
    return this.request<{
      name: {
        id: string;
        language_code: string;
        names: string[];
        is_active: boolean;
      };
    }>(`/api/admin/ai-agent/settings/names/${language}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Add new language names
   */
  async addAIAgentNames(data: { language_code: string; names: string[] }) {
    return this.request<{
      name: {
        id: string;
        language_code: string;
        names: string[];
      };
    }>('/api/admin/ai-agent/settings/names', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete language names
   */
  async deleteAIAgentNames(language: string) {
    return this.request<{ success: boolean }>(`/api/admin/ai-agent/settings/names/${language}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Get AI agent avatars
   */
  async getAIAgentAvatars() {
    return this.request<{
      avatars: Array<{
        id: string;
        name: string;
        url: string;
        is_preset: boolean;
        is_active: boolean;
        display_order: number;
      }>;
    }>('/api/admin/ai-agent/avatars', { requireAuth: true });
  }

  /**
   * Add custom AI agent avatar
   */
  async addAIAgentAvatar(data: { name: string; url: string }) {
    return this.request<{
      avatar: {
        id: string;
        name: string;
        url: string;
        is_preset: boolean;
      };
    }>('/api/admin/ai-agent/avatars', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete custom AI agent avatar
   */
  async deleteAIAgentAvatar(id: string) {
    return this.request<{ success: boolean }>(`/api/admin/ai-agent/avatars/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // ============================================
  // ESCALATION CONVERSATIONS
  // ============================================

  /**
   * Get admin escalations list
   */
  async getAdminEscalations(params?: { status?: string }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    return this.request<{
      escalations: Array<{
        id: string;
        ticket_id: string;
        summary: string;
        customer_message: string;
        ai_recommendation: string | null;
        escalation_reason: string;
        status: 'pending' | 'in_progress' | 'resolved';
        kb_candidate_count?: number;
        created_at: string;
        support_tickets?: {
          user_email: string | null;
          subject: string;
        };
      }>;
    }>(`/api/admin/ai-agent/escalations?${query.toString()}`, { requireAuth: true });
  }

  /**
   * Get single escalation details with messages
   */
  async getAdminEscalation(escalationId: string) {
    return this.request<{
      escalation: {
        id: string;
        ticket_id: string;
        summary: string;
        customer_message: string;
        ai_recommendation: string | null;
        escalation_reason: string;
        status: 'pending' | 'in_progress' | 'resolved';
        kb_suggestion: { should_create: boolean; type: 'kb' | 'faq' } | null;
        created_at: string;
        support_tickets?: {
          user_email: string | null;
          subject: string;
        };
      };
      messages: Array<{
        id: string;
        sender: 'admin' | 'ai';
        content: string;
        created_at: string;
      }>;
    }>(`/api/admin/ai-agent/escalations/${escalationId}`, { requireAuth: true });
  }

  /**
   * Send message in escalation conversation
   */
  async sendEscalationMessage(escalationId: string, content: string) {
    return this.request<{ message: { id: string; content: string } }>(
      `/api/admin/ai-agent/escalations/${escalationId}/message`,
      {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ content }),
      }
    );
  }

  /**
   * Approve escalation response
   */
  async approveEscalation(escalationId: string, finalResponse?: string) {
    return this.request<{ success: boolean }>(
      `/api/admin/ai-agent/escalations/${escalationId}/approve`,
      {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ final_response: finalResponse }),
      }
    );
  }

  // ============================================
  // EMAIL SETTINGS
  // ============================================

  /**
   * Get email settings
   */
  async getEmailSettings() {
    return this.request<{ settings: Record<string, unknown> }>(
      '/api/admin/ai-agent/email-settings',
      { requireAuth: true }
    );
  }

  /**
   * Update email settings
   */
  async updateEmailSettings(settings: Record<string, unknown>) {
    return this.request<{ success: boolean }>(
      '/api/admin/ai-agent/email-settings',
      {
        method: 'PUT',
        requireAuth: true,
        body: JSON.stringify(settings),
      }
    );
  }

  /**
   * Get email templates
   */
  async getEmailTemplates() {
    return this.request<{
      templates: Array<{
        id: string;
        template_type: string;
        subject_template: string;
        body_html: string;
        variables: string[];
        is_active: boolean;
        updated_at: string;
      }>;
    }>('/api/admin/ai-agent/email-templates', { requireAuth: true });
  }

  /**
   * Update email template
   */
  async updateEmailTemplate(
    type: string,
    data: { subject_template?: string; body_html?: string; is_active?: boolean }
  ) {
    return this.request<{
      template: {
        id: string;
        template_type: string;
        subject_template: string;
        body_html: string;
        variables: string[];
        is_active: boolean;
      };
    }>(`/api/admin/ai-agent/email-templates/${type}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Get email queue
   */
  async getEmailQueue(params?: { status?: string; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    return this.request<{
      emails: Array<{
        id: string;
        email_type: string;
        to_email: string;
        subject: string;
        status: 'pending' | 'sending' | 'sent' | 'failed';
        scheduled_send_at: string | null;
        created_at: string;
        attempts: number;
        error_message: string | null;
      }>;
    }>(`/api/admin/ai-agent/email-queue?${query.toString()}`, { requireAuth: true });
  }

  /**
   * Force send a queued email
   */
  async sendQueuedEmail(emailId: string) {
    return this.request<{ success: boolean }>(
      `/api/admin/ai-agent/email-queue/${emailId}/send`,
      {
        method: 'POST',
        requireAuth: true,
      }
    );
  }

  /**
   * Cancel a queued email
   */
  async cancelQueuedEmail(emailId: string) {
    return this.request<{ success: boolean }>(
      `/api/admin/ai-agent/email-queue/${emailId}`,
      {
        method: 'DELETE',
        requireAuth: true,
      }
    );
  }

  /**
   * Get language test runs
   */
  async getLanguageTestRuns() {
    return this.request<{
      testRuns: Array<{
        id: string;
        status: 'running' | 'completed' | 'failed';
        total_tests: number;
        completed_tests: number;
        passed_tests: number;
        failed_tests: number;
        summary_report: string | null;
        created_at: string;
        completed_at: string | null;
      }>;
    }>('/api/admin/ai-agent/test-languages', { requireAuth: true });
  }

  /**
   * Start language test run
   */
  async startLanguageTest(params?: { testMessages?: string[]; languages?: string[] }) {
    return this.request<{ success: boolean; testRunId: string }>(
      '/api/admin/ai-agent/test-languages',
      {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify(params || {}),
      }
    );
  }

  /**
   * Get language test run details
   */
  async getLanguageTestRun(testRunId: string) {
    return this.request<{
      testRun: {
        id: string;
        status: 'running' | 'completed' | 'failed';
        totalTests: number;
        completedTests: number;
        passedTests: number;
        failedTests: number;
        summaryReport: string | null;
        results: Array<{
          languageCode: string;
          languageName: string;
          reviewPassed: boolean;
          reviewScore: number | null;
          error: string | null;
        }>;
      };
    }>(`/api/admin/ai-agent/test-languages/${testRunId}`, { requireAuth: true });
  }

  // ============================================
  // SMCC (Social Media Control Center) ENDPOINTS
  // ============================================

  /**
   * Get supported SMCC platforms
   */
  async getSMCCPlatforms() {
    return this.request<{
      platforms: Array<{
        platform: string;
        configured: boolean;
        scopes: string[];
      }>;
      total: number;
    }>('/api/smcc/accounts/platforms', { requireAuth: true });
  }

  /**
   * Get all SMCC accounts
   */
  async getSMCCAccounts(params?: { platform?: string; status?: string }) {
    const query = new URLSearchParams();
    if (params?.platform) query.set('platform', params.platform);
    if (params?.status) query.set('status', params.status);
    const queryStr = query.toString();

    return this.request<{
      accounts: Array<{
        id: string;
        platform: string;
        account_name: string;
        account_id: string;
        account_type: string;
        profile_image_url: string | null;
        follower_count: number;
        following_count: number;
        bio: string | null;
        website_url: string | null;
        primary_country: string | null;
        primary_language: string | null;
        target_regions: string[] | null;
        status: string;
        last_sync_at: string | null;
        last_post_at: string | null;
        sync_error: string | null;
        token_expires_at: string | null;
        has_access_token: boolean;
        has_refresh_token: boolean;
        created_at: string;
        updated_at: string;
      }>;
      total: number;
    }>(queryStr ? `/api/smcc/accounts?${queryStr}` : '/api/smcc/accounts', { requireAuth: true });
  }

  /**
   * Get single SMCC account
   */
  async getSMCCAccount(accountId: string) {
    return this.request<{
      id: string;
      platform: string;
      account_name: string;
      account_id: string;
      account_type: string;
      profile_image_url: string | null;
      follower_count: number;
      following_count: number;
      bio: string | null;
      website_url: string | null;
      primary_country: string | null;
      primary_language: string | null;
      target_regions: string[] | null;
      status: string;
      last_sync_at: string | null;
      last_post_at: string | null;
      sync_error: string | null;
      token_expires_at: string | null;
      has_access_token: boolean;
      has_refresh_token: boolean;
      created_at: string;
      updated_at: string;
    }>(`/api/smcc/accounts/${accountId}`, { requireAuth: true });
  }

  /**
   * Get SMCC account statistics
   */
  async getSMCCAccountStats(accountId: string) {
    return this.request<{
      analytics: Array<{
        id: string;
        account_id: string;
        date: string;
        followers: number;
        followers_change: number;
        total_likes: number;
        total_comments: number;
        total_shares: number;
        impressions: number;
        reach: number;
        engagement_rate: number | null;
      }>;
      summary: {
        total_content: number;
        published_content: number;
        total_engagement: number;
        total_impressions: number;
        avg_engagement_rate: number;
      };
    }>(`/api/smcc/accounts/${accountId}/stats`, { requireAuth: true });
  }

  /**
   * Initiate SMCC OAuth connection
   */
  async connectSMCCAccount(platform: string, redirectUri?: string) {
    return this.request<{
      auth_url: string;
      state: string;
      platform: string;
      redirect_uri: string;
    }>(`/api/smcc/accounts/connect/${platform}`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ redirect_uri: redirectUri }),
    });
  }

  /**
   * Refresh SMCC account tokens
   */
  async refreshSMCCAccount(accountId: string) {
    return this.request<{
      success: boolean;
      status: string;
      token_expires_at: string | null;
    }>(`/api/smcc/accounts/${accountId}/refresh`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  /**
   * Sync SMCC account profile
   */
  async syncSMCCAccount(accountId: string) {
    return this.request<{
      success: boolean;
      account: {
        id: string;
        account_name: string;
        follower_count: number;
        following_count: number;
        last_sync_at: string;
      };
    }>(`/api/smcc/accounts/${accountId}/sync`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  /**
   * Update SMCC account settings
   */
  async updateSMCCAccount(accountId: string, data: {
    primary_country?: string;
    primary_language?: string;
    target_regions?: string[];
    account_type?: string;
  }) {
    return this.request<{ success: boolean }>(`/api/smcc/accounts/${accountId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Disconnect SMCC account
   */
  async disconnectSMCCAccount(accountId: string, permanent: boolean = false) {
    return this.request<{
      success: boolean;
      action: 'deleted' | 'disconnected';
    }>(`/api/smcc/accounts/${accountId}?permanent=${permanent}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Sync all SMCC accounts
   */
  async syncAllSMCCAccounts() {
    return this.request<{
      total: number;
      successful: number;
      failed: number;
      results: Array<{
        id: string;
        success: boolean;
        error?: string;
      }>;
    }>('/api/smcc/accounts/sync-all', {
      method: 'POST',
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC Content Management
  // ============================================================================

  async getSMCCContent(params?: {
    status?: string;
    content_type?: string;
    platform?: string;
    campaign_id?: string;
    automation_level?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.content_type) query.set('content_type', params.content_type);
    if (params?.platform) query.set('platform', params.platform);
    if (params?.campaign_id) query.set('campaign_id', params.campaign_id);
    if (params?.automation_level) query.set('automation_level', params.automation_level);
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    if (params?.sort_by) query.set('sort_by', params.sort_by);
    if (params?.sort_order) query.set('sort_order', params.sort_order);

    const queryString = query.toString();
    return this.request<{
      content: SMCCContent[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/content${queryString ? `?${queryString}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCContentById(contentId: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}`, {
      requireAuth: true,
    });
  }

  async createSMCCContent(data: {
    content_type: string;
    target_platforms: string[];
    title?: string;
    body?: string;
    media_urls?: SMCCMediaItem[];
    status?: string;
    automation_level?: string;
    scheduled_at?: string;
    campaign_id?: string;
    template_id?: string;
    brand_voice_id?: string;
    ai_generated?: boolean;
  }) {
    return this.request<{ content: SMCCContent }>('/api/smcc/content', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateSMCCContent(contentId: string, data: Partial<{
    content_type: string;
    title: string;
    body: string;
    media_urls: SMCCMediaItem[];
    target_platforms: string[];
    status: string;
    automation_level: string;
    scheduled_at: string;
    campaign_id: string;
  }>) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteSMCCContent(contentId: string) {
    return this.request<{ success: boolean }>(`/api/smcc/content/${contentId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async approveSMCCContent(contentId: string, notes?: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}/approve`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ notes }),
    });
  }

  async rejectSMCCContent(contentId: string, reason: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}/reject`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ reason }),
    });
  }

  async scheduleSMCCContent(contentId: string, scheduledAt: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}/schedule`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    });
  }

  async archiveSMCCContent(contentId: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}/archive`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async duplicateSMCCContent(contentId: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/content/${contentId}/duplicate`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async getSMCCContentVersions(contentId: string) {
    return this.request<{ versions: SMCCContentVersion[] }>(`/api/smcc/content/${contentId}/versions`, {
      requireAuth: true,
    });
  }

  async createSMCCContentVersion(contentId: string, data: {
    platform: string;
    account_id: string;
    body?: string;
    hashtags?: string[];
    mentions?: string[];
    media_urls?: SMCCMediaItem[];
    link_url?: string;
  }) {
    return this.request<{ version: SMCCContentVersion }>(`/api/smcc/content/${contentId}/versions`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async getSMCCContentAnalytics(contentId: string) {
    return this.request<{
      content: { id: string; title: string; published_at: string };
      totals: {
        likes: number;
        comments: number;
        shares: number;
        impressions: number;
        reach: number;
        engagement_rate: number;
      };
      by_platform: Record<string, { likes: number; comments: number; shares: number; impressions: number; reach: number }>;
      versions: SMCCContentVersion[];
    }>(`/api/smcc/content/${contentId}/analytics`, {
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC Calendar
  // ============================================================================

  async getSMCCCalendar(params: {
    start_date: string;
    end_date: string;
    platform?: string[];
    status?: string[];
    campaign_id?: string;
  }) {
    const query = new URLSearchParams();
    query.set('start_date', params.start_date);
    query.set('end_date', params.end_date);
    if (params.platform) {
      params.platform.forEach(p => query.append('platform', p));
    }
    if (params.status) {
      params.status.forEach(s => query.append('status', s));
    }
    if (params.campaign_id) query.set('campaign_id', params.campaign_id);

    return this.request<{
      items: SMCCCalendarItem[];
      by_date: Record<string, SMCCCalendarItem[]>;
      start_date: string;
      end_date: string;
      total: number;
    }>(`/api/smcc/calendar?${query.toString()}`, {
      requireAuth: true,
    });
  }

  async rescheduleSMCCContent(contentId: string, scheduledAt: string) {
    return this.request<{ content: SMCCContent }>('/api/smcc/calendar/reschedule', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ content_id: contentId, scheduled_at: scheduledAt }),
    });
  }

  async getSMCCOptimalSlots(platform?: string, date?: string) {
    const query = new URLSearchParams();
    if (platform) query.set('platform', platform);
    if (date) query.set('date', date);

    const queryString = query.toString();
    return this.request<{
      platform?: string;
      date?: string;
      slots: string[] | Record<string, string[]>;
      source: string;
    }>(`/api/smcc/calendar/slots${queryString ? `?${queryString}` : ''}`, {
      requireAuth: true,
    });
  }

  async bulkScheduleSMCCContent(items: Array<{ content_id: string; scheduled_at: string }>) {
    return this.request<{
      total: number;
      successful: number;
      failed: number;
      results: Array<{ content_id: string; success: boolean; error?: string }>;
    }>('/api/smcc/calendar/bulk-schedule', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ items }),
    });
  }

  // ============================================================================
  // SMCC AI - Content Generation & Analysis
  // ============================================================================

  async generateSMCCContent(params: {
    prompt: string;
    contentType: string;
    targetPlatforms: string[];
    brandVoiceId?: string;
    templateId?: string;
    variables?: Record<string, string>;
    targetLanguage?: string;
    includeHashtags?: boolean;
    includeEmojis?: boolean;
    maxLength?: number;
  }) {
    return this.request<{
      title?: string;
      body: string;
      hashtags: string[];
      suggestedMedia?: string;
      platformVersions: Record<string, {
        body: string;
        hashtags: string[];
        characterCount: number;
      }>;
      aiProvider: string;
      aiModel: string;
    }>('/api/smcc/ai/generate', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  async adaptSMCCContent(params: {
    content: string;
    sourcePlatform?: string;
    targetPlatform: string;
    brandVoiceId?: string;
    preserveHashtags?: boolean;
    targetLanguage?: string;
  }) {
    return this.request<{
      body: string;
      hashtags: string[];
      characterCount: number;
      truncated: boolean;
      suggestions?: string[];
    }>('/api/smcc/ai/adapt', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  async localizeSMCCContent(params: {
    content: string;
    sourceLanguage: string;
    targetLanguages: string[];
    brandVoiceId?: string;
    preserveTone?: boolean;
    adaptHashtags?: boolean;
  }) {
    return this.request<{
      translations: Record<string, {
        body: string;
        hashtags?: string[];
      }>;
      aiProvider: string;
    }>('/api/smcc/ai/localize', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  async suggestSMCCHashtags(content: string, platform: string, count?: number) {
    return this.request<{
      hashtags: string[];
    }>('/api/smcc/ai/suggest-hashtags', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ content, platform, count }),
    });
  }

  async analyzeSMCCSentiment(text: string) {
    return this.request<{
      sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
      score: number;
      confidence: number;
      topics: string[];
      intent: string;
      urgency: 'low' | 'medium' | 'high';
      requiresResponse: boolean;
    }>('/api/smcc/ai/analyze-sentiment', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ text }),
    });
  }

  async suggestSMCCResponses(params: {
    message: string;
    context?: {
      senderUsername?: string;
      platform?: string;
      postContent?: string;
      previousMessages?: string[];
    };
    brandVoiceId?: string;
    count?: number;
  }) {
    return this.request<{
      suggestions: Array<{
        text: string;
        tone: string;
        confidence: number;
      }>;
    }>('/api/smcc/ai/suggest-responses', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  async generateSMCCContentVariations(content: string, count?: number, brandVoiceId?: string) {
    return this.request<{
      variations: Array<{
        body: string;
        variation: string;
      }>;
    }>('/api/smcc/ai/variations', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ content, count, brandVoiceId }),
    });
  }

  // Brand Voice Management

  async getSMCCBrandVoices() {
    return this.request<{
      brandVoices: Array<{
        id: string;
        name: string;
        description?: string;
        tone: string;
        personality_traits: string[];
        vocabulary_preferences: { prefer?: string[]; avoid?: string[] };
        emoji_usage: string;
        hashtag_style: string;
        sample_posts?: string[];
        is_default: boolean;
        is_active: boolean;
        created_at: string;
      }>;
    }>('/api/smcc/ai/brand-voices', {
      requireAuth: true,
    });
  }

  async createSMCCBrandVoice(brandVoice: {
    name: string;
    description?: string;
    tone: string;
    personality_traits?: string[];
    vocabulary_preferences?: { prefer?: string[]; avoid?: string[] };
    emoji_usage?: string;
    hashtag_style?: string;
    sample_posts?: string[];
    is_default?: boolean;
  }) {
    return this.request<{ brandVoice: SMCCBrandVoice }>('/api/smcc/ai/brand-voices', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(brandVoice),
    });
  }

  async updateSMCCBrandVoice(id: string, updates: Partial<{
    name: string;
    description: string;
    tone: string;
    personality_traits: string[];
    vocabulary_preferences: { prefer?: string[]; avoid?: string[] };
    emoji_usage: string;
    hashtag_style: string;
    sample_posts: string[];
    is_default: boolean;
  }>) {
    return this.request<{ brandVoice: SMCCBrandVoice }>(`/api/smcc/ai/brand-voices/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(updates),
    });
  }

  async deleteSMCCBrandVoice(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/ai/brand-voices/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Platform Helpers

  async getSMCCPlatformDetails() {
    return this.request<{
      platforms: Array<{
        platform: string;
        constraints: {
          max_caption_length: number;
          max_hashtags: number;
          max_mentions: number;
          supported_media: string[];
          max_images: number;
          max_video_duration: number;
          supports_links: boolean;
          supports_scheduling: boolean;
        };
        contentTypes: string[];
      }>;
    }>('/api/smcc/ai/platforms', {
      requireAuth: true,
    });
  }

  async getSMCCPlatformConstraints(platform: string) {
    return this.request<{
      platform: string;
      constraints: {
        max_caption_length: number;
        max_hashtags: number;
        max_mentions: number;
        supported_media: string[];
        max_images: number;
        max_video_duration: number;
        supports_links: boolean;
        supports_scheduling: boolean;
      };
      bestPractices: string[];
      compatibleContentTypes: string[];
      optimalPostingTimes: Array<{
        hour: number;
        day: number;
        score: number;
      }>;
    }>(`/api/smcc/ai/platforms/${platform}/constraints`, {
      requireAuth: true,
    });
  }

  async validateSMCCContent(platform: string, params: {
    body: string;
    hashtags?: string[];
    mentions?: string[];
    media?: SMCCMediaItem[];
  }) {
    return this.request<{
      isValid: boolean;
      errors: string[];
      warnings: string[];
      suggestions: string[];
    }>(`/api/smcc/ai/platforms/${platform}/validate`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  async quickAdaptSMCCContent(platform: string, params: {
    body: string;
    hashtags?: string[];
    mentions?: string[];
  }) {
    return this.request<{
      body: string;
      hashtags: string[];
      mentions: string[];
      truncated: boolean;
      characterCount: number;
      hashtagCount: number;
      validation: {
        isValid: boolean;
        errors: string[];
        warnings: string[];
        suggestions: string[];
      };
    }>(`/api/smcc/ai/platforms/${platform}/quick-adapt`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(params),
    });
  }

  // ============================================================================
  // SMCC Engagement - Inbox & Auto-Response
  // ============================================================================

  async getSMCCInbox(params?: {
    status?: string;
    item_type?: string;
    platform?: string;
    account_id?: string;
    sentiment?: string;
    intent?: string;
    priority?: string;
    requires_response?: string;
    assigned_to?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request<{
      items: SMCCInboxItem[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/engagement/inbox${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCInboxStats(accountIds?: string[]) {
    const searchParams = new URLSearchParams();
    if (accountIds && accountIds.length > 0) {
      searchParams.append('account_ids', accountIds.join(','));
    }
    const query = searchParams.toString();
    return this.request<{
      stats: {
        total_items: number;
        total_unread: number;
        urgent_count: number;
        high_priority_count: number;
        negative_sentiment_count: number;
        needs_response_count: number;
        avg_response_time_hours: number;
        response_rate: number;
        items_today: number;
        items_this_week: number;
      };
    }>(`/api/smcc/engagement/inbox/stats${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCInboxItem(id: string) {
    return this.request<{ item: SMCCInboxItem }>(`/api/smcc/engagement/inbox/${id}`, {
      requireAuth: true,
    });
  }

  async updateSMCCInboxItem(id: string, data: {
    status?: string;
    priority?: string;
    assigned_to?: string | null;
  }) {
    return this.request<{ item: SMCCInboxItem }>(`/api/smcc/engagement/inbox/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async replySMCCInbox(id: string, content: string, responseMethod?: string) {
    return this.request<{
      item: SMCCInboxItem;
      platform_response_id: string;
    }>(`/api/smcc/engagement/inbox/${id}/reply`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ content, response_method: responseMethod }),
    });
  }

  async hideSMCCInboxComment(id: string) {
    return this.request<{ success: boolean; item: SMCCInboxItem }>(`/api/smcc/engagement/inbox/${id}/hide`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async flagSMCCInboxItem(id: string, reason?: string) {
    return this.request<{ item: SMCCInboxItem }>(`/api/smcc/engagement/inbox/${id}/flag`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ reason }),
    });
  }

  async analyzeSMCCInboxItem(id: string) {
    return this.request<{
      item: SMCCInboxItem;
      analysis: {
        sentiment: { score: number; label: SMCCSentiment };
        suggestions: SMCCSuggestedResponse[];
      };
    }>(`/api/smcc/engagement/inbox/${id}/analyze`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async bulkUpdateSMCCInbox(ids: string[], action: string, assignedTo?: string) {
    return this.request<{
      updated: number;
      items: SMCCInboxItem[];
    }>(`/api/smcc/engagement/inbox/bulk`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ ids, action, assigned_to: assignedTo }),
    });
  }

  async getSMCCConversation(conversationId: string) {
    return this.request<{ messages: SMCCConversationMessage[] }>(`/api/smcc/engagement/conversations/${conversationId}`, {
      requireAuth: true,
    });
  }

  // Auto-response rules
  async getSMCCAutoResponseRules(isActive?: boolean) {
    const params = isActive !== undefined ? `?is_active=${isActive}` : '';
    return this.request<{ rules: SMCCAutoResponseRule[] }>(`/api/smcc/engagement/rules${params}`, {
      requireAuth: true,
    });
  }

  async createSMCCAutoResponseRule(rule: {
    name: string;
    description?: string;
    trigger_conditions: Record<string, unknown>;
    response_type: string;
    response_template?: string;
    ai_prompt_override?: string;
    platforms?: string[];
    accounts?: string[];
    item_types?: string[];
    delay_seconds?: number;
    max_uses_per_day?: number;
    priority?: number;
    is_active?: boolean;
  }) {
    return this.request<{ rule: SMCCAutoResponseRule }>(`/api/smcc/engagement/rules`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(rule),
    });
  }

  async updateSMCCAutoResponseRule(id: string, updates: Partial<{
    name: string;
    description: string;
    trigger_conditions: Record<string, unknown>;
    response_type: string;
    response_template: string;
    ai_prompt_override: string;
    platforms: string[];
    accounts: string[];
    item_types: string[];
    delay_seconds: number;
    max_uses_per_day: number;
    priority: number;
    is_active: boolean;
  }>) {
    return this.request<{ rule: SMCCAutoResponseRule }>(`/api/smcc/engagement/rules/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(updates),
    });
  }

  async deleteSMCCAutoResponseRule(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/engagement/rules/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async testSMCCAutoResponseRule(id: string, testData: {
    content: string;
    item_type: string;
    platform?: string;
    sender_username?: string;
  }) {
    return this.request<{
      would_trigger: boolean;
      matches: string[];
      sample_response: string | null;
      response_type: string;
    }>(`/api/smcc/engagement/rules/${id}/test`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(testData),
    });
  }

  async getSMCCModerationLog(params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request<{
      logs: SMCCModerationLog[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/engagement/moderation-log${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC Analytics
  // ============================================================================

  async getSMCCAnalyticsDashboard(params?: {
    period?: '24h' | '7d' | '30d' | '90d';
    account_ids?: string[];
  }) {
    const searchParams = new URLSearchParams();
    if (params?.period) searchParams.append('period', params.period);
    if (params?.account_ids?.length) searchParams.append('account_ids', params.account_ids.join(','));
    const query = searchParams.toString();
    return this.request<{
      metrics: {
        total_followers: number;
        followers_change: number;
        followers_change_percent: number;
        total_impressions: number;
        impressions_change_percent: number;
        total_reach: number;
        reach_change_percent: number;
        total_engagement: number;
        engagement_change_percent: number;
        avg_engagement_rate: number;
        posts_published: number;
        profile_views: number;
        website_clicks: number;
        pending_approval_count: number;
        scheduled_count: number;
        top_platform?: string;
      };
      period: string;
      start_date: string;
      end_date: string;
    }>(`/api/smcc/analytics/dashboard${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCAnalyticsTimeline(params?: {
    start_date?: string;
    end_date?: string;
    account_ids?: string[];
    platform?: string;
    metric?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      if (params.start_date) searchParams.append('start_date', params.start_date);
      if (params.end_date) searchParams.append('end_date', params.end_date);
      if (params.account_ids?.length) searchParams.append('account_ids', params.account_ids.join(','));
      if (params.platform) searchParams.append('platform', params.platform);
      if (params.metric) searchParams.append('metric', params.metric);
    }
    const query = searchParams.toString();
    return this.request<{
      timeline: Array<{
        date: string;
        followers: number;
        followers_change: number;
        impressions: number;
        reach: number;
        engagement: number;
        likes: number;
        comments: number;
        shares: number;
        posts_published: number;
      }>;
      metric: string;
      start_date: string;
      end_date: string;
    }>(`/api/smcc/analytics/timeline${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCAnalyticsAccount(accountId: string, params?: {
    start_date?: string;
    end_date?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    const query = searchParams.toString();
    return this.request<{
      account: { id: string; platform: string; account_name: string; profile_image_url?: string };
      summary: {
        current_followers: number;
        total_followers_change: number;
        total_impressions: number;
        total_reach: number;
        total_engagement: number;
        total_likes: number;
        total_comments: number;
        total_shares: number;
        posts_published: number;
        avg_engagement_rate: number;
      };
      daily: SMCCAnalyticsDaily[];
      start_date: string;
      end_date: string;
    }>(`/api/smcc/analytics/accounts/${accountId}${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCAnalyticsPlatforms(params?: {
    start_date?: string;
    end_date?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    const query = searchParams.toString();
    return this.request<{
      platforms: Array<{
        platform: string;
        followers: number;
        followers_change: number;
        impressions: number;
        reach: number;
        engagement: number;
        likes: number;
        comments: number;
        shares: number;
        posts_published: number;
        engagement_rate: number;
        account_count: number;
      }>;
      start_date: string;
      end_date: string;
    }>(`/api/smcc/analytics/platforms${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCAnalyticsTopContent(params?: {
    start_date?: string;
    end_date?: string;
    platform?: string;
    sort_by?: string;
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      if (params.start_date) searchParams.append('start_date', params.start_date);
      if (params.end_date) searchParams.append('end_date', params.end_date);
      if (params.platform) searchParams.append('platform', params.platform);
      if (params.sort_by) searchParams.append('sort_by', params.sort_by);
      if (params.limit) searchParams.append('limit', String(params.limit));
    }
    const query = searchParams.toString();
    return this.request<{
      content: SMCCTopContent[];
      sort_by: string;
      start_date: string;
      end_date: string;
    }>(`/api/smcc/analytics/content/top${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async exportSMCCAnalytics(params?: {
    start_date?: string;
    end_date?: string;
    format?: 'json' | 'csv';
    account_ids?: string[];
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      if (params.start_date) searchParams.append('start_date', params.start_date);
      if (params.end_date) searchParams.append('end_date', params.end_date);
      if (params.format) searchParams.append('format', params.format);
      if (params.account_ids?.length) searchParams.append('account_ids', params.account_ids.join(','));
    }
    const query = searchParams.toString();

    if (params?.format === 'csv') {
      // For CSV, return raw response to download
      const accessToken = await this.getToken();
      const response = await fetch(`${this.baseUrl}/api/smcc/analytics/export?${query}`, {
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
      });
      return response;
    }

    return this.request<{
      data: SMCCExportDataItem[];
      start_date: string;
      end_date: string;
      exported_at: string;
    }>(`/api/smcc/analytics/export${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC Campaigns
  // ============================================================================

  async getSMCCCampaigns(params?: {
    status?: string;
    goal_type?: string;
    platform?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    return this.request<{
      campaigns: SMCCCampaign[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/campaigns${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCCampaign(id: string) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}`, {
      requireAuth: true,
    });
  }

  async createSMCCCampaign(data: {
    name: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    goal_type?: string;
    target_metrics?: Record<string, number>;
    budget?: number;
    currency?: string;
    target_platforms?: string[];
    target_regions?: string[];
    status?: string;
  }) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async updateSMCCCampaign(id: string, data: Partial<{
    name: string;
    description: string;
    start_date: string;
    end_date: string;
    goal_type: string;
    target_metrics: Record<string, number>;
    budget: number;
    currency: string;
    target_platforms: string[];
    target_regions: string[];
    status: string;
  }>) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  async deleteSMCCCampaign(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/campaigns/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async getSMCCCampaignContent(id: string, params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', String(params.limit));
    if (params?.offset) searchParams.append('offset', String(params.offset));
    const query = searchParams.toString();
    return this.request<{
      content: SMCCContent[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/campaigns/${id}/content${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async addContentToCampaign(campaignId: string, contentIds: string[]) {
    return this.request<{
      added: number;
      content: SMCCContent[];
    }>(`/api/smcc/campaigns/${campaignId}/content`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ content_ids: contentIds }),
    });
  }

  async removeContentFromCampaign(campaignId: string, contentId: string) {
    return this.request<{ content: SMCCContent }>(`/api/smcc/campaigns/${campaignId}/content/${contentId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async getSMCCCampaignAnalytics(id: string) {
    return this.request<{
      campaign: SMCCCampaign;
      analytics: {
        total_content: number;
        content_by_status: Record<string, number>;
        total_likes: number;
        total_comments: number;
        total_shares: number;
        total_impressions: number;
        total_reach: number;
        avg_engagement_rate: number;
        budget_used_percent: number;
      };
      goal_progress: Record<string, { target: number; current: number; percent: number }>;
    }>(`/api/smcc/campaigns/${id}/analytics`, {
      requireAuth: true,
    });
  }

  async duplicateSMCCCampaign(id: string) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}/duplicate`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async activateSMCCCampaign(id: string) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}/activate`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async pauseSMCCCampaign(id: string) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}/pause`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  async completeSMCCCampaign(id: string) {
    return this.request<{ campaign: SMCCCampaign }>(`/api/smcc/campaigns/${id}/complete`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC ASSET LIBRARY
  // ============================================================================

  async getSMCCAssets(params?: {
    asset_type?: string;
    folder_path?: string;
    search?: string;
    tags?: string[];
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.asset_type) searchParams.set('asset_type', params.asset_type);
    if (params?.folder_path) searchParams.set('folder_path', params.folder_path);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tags?.length) searchParams.set('tags', params.tags.join(','));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order);

    const query = searchParams.toString();
    return this.request<{
      assets: SMCCAsset[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/assets${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async uploadSMCCAsset(formData: FormData) {
    return this.request<{ asset: SMCCAsset }>(`/api/smcc/assets/upload`, {
      method: 'POST',
      body: formData,
      requireAuth: true,
      isFormData: true,
    });
  }

  async getSMCCAsset(id: string) {
    return this.request<{ asset: SMCCAsset }>(`/api/smcc/assets/${id}`, {
      requireAuth: true,
    });
  }

  async updateSMCCAsset(id: string, data: {
    name?: string;
    description?: string;
    alt_text?: string;
    tags?: string[];
    folder_path?: string;
    attribution?: string;
  }) {
    return this.request<{ asset: SMCCAsset }>(`/api/smcc/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async deleteSMCCAsset(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/assets/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  async getSMCCAssetFolders() {
    return this.request<{
      folders: Array<{ path: string; count: number }>;
    }>(`/api/smcc/assets/folders`, {
      requireAuth: true,
    });
  }

  async createSMCCAssetFolder(path: string) {
    return this.request<{
      success: boolean;
      folder: { path: string; count: number };
    }>(`/api/smcc/assets/folders`, {
      method: 'POST',
      body: JSON.stringify({ path }),
      requireAuth: true,
    });
  }

  async bulkMoveSMCCAssets(assetIds: string[], folderPath: string) {
    return this.request<{ success: boolean; moved: number }>(`/api/smcc/assets/bulk/move`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds, folder_path: folderPath }),
      requireAuth: true,
    });
  }

  async bulkDeleteSMCCAssets(assetIds: string[]) {
    return this.request<{ success: boolean; deleted: number }>(`/api/smcc/assets/bulk/delete`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds }),
      requireAuth: true,
    });
  }

  async bulkTagSMCCAssets(assetIds: string[], tags: string[], mode: 'add' | 'replace') {
    return this.request<{ success: boolean; updated: number }>(`/api/smcc/assets/bulk/tag`, {
      method: 'POST',
      body: JSON.stringify({ asset_ids: assetIds, tags, mode }),
      requireAuth: true,
    });
  }

  // UGC (User-Generated Content) methods
  async getSMCCUGC(params?: {
    platform?: string;
    consent_status?: string;
    is_featured?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: string;
    sort_order?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.platform) searchParams.set('platform', params.platform);
    if (params?.consent_status) searchParams.set('consent_status', params.consent_status);
    if (params?.is_featured !== undefined) searchParams.set('is_featured', String(params.is_featured));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order);

    const query = searchParams.toString();
    return this.request<{
      submissions: SMCCUGCSubmission[];
      total: number;
      limit: number;
      offset: number;
    }>(`/api/smcc/assets/ugc${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  async getSMCCUGCItem(id: string) {
    return this.request<{ submission: SMCCUGCSubmission }>(`/api/smcc/assets/ugc/${id}`, {
      requireAuth: true,
    });
  }

  async requestSMCCUGCConsent(id: string, message?: string) {
    return this.request<{ submission: SMCCUGCSubmission }>(`/api/smcc/assets/ugc/${id}/consent`, {
      method: 'POST',
      body: JSON.stringify({ message }),
      requireAuth: true,
    });
  }

  async updateSMCCUGCStatus(id: string, data: {
    consent_status?: string;
    is_featured?: boolean;
    featured_on_platforms?: string[];
    quality_score?: number;
  }) {
    return this.request<{ submission: SMCCUGCSubmission }>(`/api/smcc/assets/ugc/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async deleteSMCCUGC(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/assets/ugc/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // ============================================================================
  // SMCC SETTINGS
  // ============================================================================

  // Compliance Rules
  async getSMCCComplianceRules() {
    return this.request<{ rules: SMCCComplianceRule[] }>(`/api/smcc/settings/compliance-rules`, {
      requireAuth: true,
    });
  }

  async createSMCCComplianceRule(data: {
    name: string;
    description?: string;
    rule_type: string;
    config?: Record<string, unknown>;
    platforms?: string[];
    content_types?: string[];
    violation_action?: string;
    severity?: string;
    is_active?: boolean;
  }) {
    return this.request<{ rule: SMCCComplianceRule }>(`/api/smcc/settings/compliance-rules`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async updateSMCCComplianceRule(id: string, data: Partial<{
    name: string;
    description: string;
    rule_type: string;
    config: Record<string, unknown>;
    platforms: string[];
    content_types: string[];
    violation_action: string;
    severity: string;
    is_active: boolean;
  }>) {
    return this.request<{ rule: SMCCComplianceRule }>(`/api/smcc/settings/compliance-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async deleteSMCCComplianceRule(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/settings/compliance-rules/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // Kill Switch
  async getSMCCKillSwitch() {
    return this.request<{
      global_active: boolean;
      active_switches: SMCCKillSwitch[];
    }>(`/api/smcc/settings/kill-switch`, {
      requireAuth: true,
    });
  }

  async activateSMCCKillSwitch(data: {
    scope: 'global' | 'platform' | 'account' | 'campaign';
    scope_id?: string;
    platform?: string;
    reason: string;
  }) {
    return this.request<{ killSwitch: SMCCKillSwitch }>(`/api/smcc/settings/kill-switch`, {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async deactivateSMCCKillSwitch(id: string) {
    return this.request<{ success: boolean }>(`/api/smcc/settings/kill-switch/${id}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // General Settings
  async getSMCCGeneralSettings() {
    return this.request<{ settings: Record<string, unknown> }>(`/api/smcc/settings/general`, {
      requireAuth: true,
    });
  }

  async updateSMCCGeneralSettings(settings: Record<string, unknown>) {
    return this.request<{ settings: Record<string, unknown> }>(`/api/smcc/settings/general`, {
      method: 'PUT',
      body: JSON.stringify(settings),
      requireAuth: true,
    });
  }

  // ============================================================================
  // MARKETPLACE - Listings
  // ============================================================================

  /**
   * Get user's own marketplace listings
   */
  async getMyMarketplaceListings(params?: {
    status?: 'active' | 'pending' | 'sold' | 'expired' | 'removed';
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      data: Array<MarketplaceListing>;
      pagination: { total: number | null; limit: number; offset: number; hasMore: boolean };
    }>(`/api/marketplace/listings/my${queryStr ? `?${queryStr}` : ''}`, { requireAuth: true });
  }

  /**
   * Get listings matching a product code within user's radius
   */
  async getMatchingMarketplaceListings(productCode: string) {
    return this.request<{
      success: boolean;
      data: Array<MarketplaceListing & { distance_km?: number }>;
      message?: string;
    }>(`/api/marketplace/listings/matching/${productCode}`, { requireAuth: true });
  }

  /**
   * Get listing counts for multiple product codes (for checklist badges)
   */
  async getMarketplaceListingCounts(productCodes: string[]) {
    return this.request<{
      success: boolean;
      data: Record<string, { count: number; min_price: number | null; max_discount: number | null }>;
    }>('/api/marketplace/listings/counts', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ product_codes: productCodes }),
    });
  }

  /**
   * Get a single marketplace listing by ID
   */
  async getMarketplaceListing(listingId: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceListing & {
        seller_stats: { total_sales: number; seller_avg_overall: number | null; seller_rating_count: number };
        distance_km: number | null;
        is_own_listing: boolean;
      };
    }>(`/api/marketplace/listings/${listingId}`, { requireAuth: true });
  }

  /**
   * Create a new marketplace listing
   */
  async createMarketplaceListing(data: {
    product_code: string;
    asking_price: number;
    retail_price?: number;
    condition: 'excellent' | 'good' | 'fair';
    assembly_status: 'flatpack_sealed' | 'flatpack_opened' | 'assembled' | 'disassembled';
    times_assembled?: number;
    missing_parts?: string;
    modifications?: string;
    description?: string;
    photos?: string[];
    postal_code?: string;
    neighborhood?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplaceListing;
    }>('/api/marketplace/listings', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a marketplace listing
   */
  async updateMarketplaceListing(listingId: string, data: {
    asking_price?: number;
    condition?: 'excellent' | 'good' | 'fair';
    assembly_status?: 'flatpack_sealed' | 'flatpack_opened' | 'assembled' | 'disassembled';
    times_assembled?: number;
    missing_parts?: string;
    modifications?: string;
    description?: string;
    photos?: string[];
    postal_code?: string;
    neighborhood?: string;
    status?: 'active' | 'pending' | 'sold' | 'expired' | 'removed';
    latitude?: number;
    longitude?: number;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplaceListing;
    }>(`/api/marketplace/listings/${listingId}`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Mark a listing as sold
   */
  async markMarketplaceListingSold(listingId: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceListing;
    }>(`/api/marketplace/listings/${listingId}/sold`, {
      method: 'POST',
      requireAuth: true,
    });
  }

  /**
   * Delete (remove) a marketplace listing
   */
  async deleteMarketplaceListing(listingId: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>(`/api/marketplace/listings/${listingId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Search/browse marketplace listings
   */
  async searchMarketplaceListings(params?: {
    product_code?: string;
    condition?: 'excellent' | 'good' | 'fair';
    max_price?: number;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.product_code) query.set('product_code', params.product_code);
    if (params?.condition) query.set('condition', params.condition);
    if (params?.max_price) query.set('max_price', params.max_price.toString());
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      data: Array<MarketplaceListing>;
      pagination: { total: number | null; limit: number; offset: number; hasMore: boolean };
    }>(`/api/marketplace/listings${queryStr ? `?${queryStr}` : ''}`, { requireAuth: true });
  }

  // ============================================================================
  // MARKETPLACE - Chat
  // ============================================================================

  /**
   * Get all marketplace conversations
   */
  async getMarketplaceConversations(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      data: Array<MarketplaceConversation>;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/api/marketplace/chat/conversations${queryStr ? `?${queryStr}` : ''}`, { requireAuth: true });
  }

  /**
   * Get a specific conversation with messages
   */
  async getMarketplaceConversation(conversationId: string, params?: { limit?: number; before?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.before) query.set('before', params.before);
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      data: {
        conversation: MarketplaceConversation & { is_buyer: boolean };
        listing: MarketplaceListing;
        other_party: { id: string; email: string; stats?: MarketplaceUserStats };
        messages: Array<MarketplaceMessage>;
      };
    }>(`/api/marketplace/chat/conversations/${conversationId}${queryStr ? `?${queryStr}` : ''}`, { requireAuth: true });
  }

  /**
   * Start a new conversation about a listing
   */
  async startMarketplaceConversation(listingId: string, initialMessage: string) {
    return this.request<{
      success: boolean;
      data: { conversation_id: string; message: MarketplaceMessage };
      existing: boolean;
    }>('/api/marketplace/chat/conversations', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ listing_id: listingId, initial_message: initialMessage }),
    });
  }

  /**
   * Send a message in a conversation
   */
  async sendMarketplaceMessage(conversationId: string, content: string, messageType?: 'text' | 'image') {
    return this.request<{
      success: boolean;
      data: MarketplaceMessage;
    }>(`/api/marketplace/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify({ content, message_type: messageType || 'text' }),
    });
  }

  /**
   * Mark conversation as read
   */
  async markMarketplaceConversationRead(conversationId: string) {
    return this.request<{ success: boolean }>(`/api/marketplace/chat/conversations/${conversationId}/read`, {
      method: 'PUT',
      requireAuth: true,
    });
  }

  /**
   * Archive a conversation
   */
  async archiveMarketplaceConversation(conversationId: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>(`/api/marketplace/chat/conversations/${conversationId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Get total unread message count
   */
  async getMarketplaceUnreadCount() {
    return this.request<{
      success: boolean;
      data: { unread_count: number };
    }>('/api/marketplace/chat/unread-count', { requireAuth: true });
  }

  // ============================================================================
  // MARKETPLACE - Transactions
  // ============================================================================

  /**
   * Get user's marketplace transactions
   */
  async getMarketplaceTransactions(params?: {
    role?: 'buyer' | 'seller';
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.role) query.set('role', params.role);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      data: Array<MarketplaceTransaction>;
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>(`/api/marketplace/transactions${queryStr ? `?${queryStr}` : ''}`, { requireAuth: true });
  }

  /**
   * Get a specific transaction
   */
  async getMarketplaceTransaction(transactionId: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceTransaction & {
        listing: MarketplaceListing;
        other_party: { id: string; email: string };
        is_buyer: boolean;
        has_rated: boolean;
      };
    }>(`/api/marketplace/transactions/${transactionId}`, { requireAuth: true });
  }

  /**
   * Create a transaction from a conversation
   */
  async createMarketplaceTransaction(data: {
    conversation_id: string;
    agreed_price: number;
    meetup_location?: string;
    meetup_time?: string;
    meetup_notes?: string;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplaceTransaction;
    }>('/api/marketplace/transactions', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Confirm transaction completion
   */
  async confirmMarketplaceTransaction(transactionId: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceTransaction;
    }>(`/api/marketplace/transactions/${transactionId}/confirm`, {
      method: 'PUT',
      requireAuth: true,
    });
  }

  /**
   * Cancel a transaction
   */
  async cancelMarketplaceTransaction(transactionId: string, reason?: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceTransaction;
    }>(`/api/marketplace/transactions/${transactionId}/cancel`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Report no-show for a transaction
   */
  async reportMarketplaceNoShow(transactionId: string, description?: string) {
    return this.request<{
      success: boolean;
      data: MarketplaceTransaction;
    }>(`/api/marketplace/transactions/${transactionId}/no-show`, {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify({ description }),
    });
  }

  /**
   * Submit a rating for a completed transaction
   */
  async rateMarketplaceTransaction(transactionId: string, data: {
    overall_rating: number;
    item_accuracy_rating?: number;
    communication_rating?: number;
    reliability_rating?: number;
    comment?: string;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplaceRating;
    }>(`/api/marketplace/transactions/${transactionId}/rate`, {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Get a user's marketplace profile (public)
   */
  async getMarketplaceUserProfile(userId: string) {
    return this.request<{
      success: boolean;
      data: {
        user_id: string;
        email?: string;
        total_sales: number;
        total_purchases: number;
        seller_avg_overall: number | null;
        buyer_avg_overall: number | null;
        seller_rating_count: number;
        buyer_rating_count: number;
        active_listings_count: number;
        recent_ratings: Array<MarketplaceRating>;
      };
    }>(`/api/marketplace/transactions/users/${userId}/profile`, { requireAuth: true });
  }

  // ============================================================================
  // MARKETPLACE - Preferences
  // ============================================================================

  /**
   * Get user's marketplace preferences
   */
  async getMarketplacePreferences() {
    return this.request<{
      success: boolean;
      data: MarketplacePreferences;
    }>('/api/marketplace/preferences', { requireAuth: true });
  }

  /**
   * Update marketplace preferences
   */
  async updateMarketplacePreferences(data: {
    search_radius_km?: number;
    accept_excellent?: boolean;
    accept_good?: boolean;
    accept_fair?: boolean;
    accept_flatpack_only?: boolean;
    accept_assembled?: boolean;
    notify_new_matches?: boolean;
    notify_price_drops?: boolean;
    notify_messages?: boolean;
    search_postal_code?: string;
    search_country?: string;
    search_latitude?: number;
    search_longitude?: number;
    seller_postal_code?: string;
    seller_neighborhood?: string;
    seller_latitude?: number;
    seller_longitude?: number;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplacePreferences;
    }>('/api/marketplace/preferences', {
      method: 'PUT',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Set search location by postal code
   */
  async setMarketplaceSearchLocation(data: {
    postal_code: string;
    country: string;
    latitude: number;
    longitude: number;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplacePreferences;
    }>('/api/marketplace/preferences/set-search-location', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Set seller location by postal code
   */
  async setMarketplaceSellerLocation(data: {
    postal_code: string;
    neighborhood?: string;
    latitude: number;
    longitude: number;
  }) {
    return this.request<{
      success: boolean;
      data: MarketplacePreferences;
    }>('/api/marketplace/preferences/set-seller-location', {
      method: 'POST',
      requireAuth: true,
      body: JSON.stringify(data),
    });
  }

  /**
   * Get radius options for marketplace search
   */
  async getMarketplaceRadiusOptions() {
    return this.request<{
      success: boolean;
      data: Array<{ value: number; label: string }>;
    }>('/api/marketplace/preferences/radius-options', { requireAuth: true });
  }

  // =============================================================================
  // ADMIN REPORTS
  // =============================================================================

  /**
   * Get revenue report with MRR, ARR, and tier breakdown
   */
  async getAdminRevenueReport(params: { start?: string; end?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();
    return this.request<{
      mrr: { current: number; change: number; previousChange: number; changePercent: number };
      arr: number;
      byTier: Record<string, { count: number; mrr: number }>;
      byInterval: Record<string, { count: number; mrr: number }>;
      byPlatform: Record<string, { count: number; mrr: number }>;
      period: { start: string; end: string };
    }>(`/api/admin/reports/revenue${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Get revenue history for charts
   */
  async getAdminRevenueHistory(params: { start?: string; end?: string; period?: 'daily' | 'weekly' | 'monthly' } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    if (params.period) query.set('period', params.period);
    const queryString = query.toString();
    return this.request<{
      history: Array<{
        date: string;
        mrrChange: number;
        cumulativeMrr: number;
        newSubs: number;
        churned: number;
        upgrades: number;
        downgrades: number;
      }>;
      period: string;
    }>(`/api/admin/reports/revenue/history${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Get subscription metrics
   */
  async getAdminSubscriptionMetrics(params: { start?: string; end?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();
    return this.request<{
      active: number;
      new: number;
      newChange: number;
      churned: number;
      churnedChange: number;
      upgrades: number;
      downgrades: number;
      renewals: number;
      paymentFailures: number;
      reactivations: number;
      churnRate: number;
      period: { start: string; end: string };
    }>(`/api/admin/reports/subscriptions${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Get subscription events list with pagination
   */
  async getAdminSubscriptionEvents(params: {
    start?: string;
    end?: string;
    page?: number;
    limit?: number;
    event_type?: string;
  } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.event_type) query.set('event_type', params.event_type);
    const queryString = query.toString();
    return this.request<{
      events: Array<{
        id: string;
        user_id: string;
        user_email: string;
        event_type: string;
        previous_tier: string | null;
        new_tier: string;
        billing_interval: string | null;
        amount_cents: number | null;
        platform: string;
        mrr_change_cents: number | null;
        created_at: string;
      }>;
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/api/admin/reports/subscriptions/events${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Get user growth and analytics
   */
  async getAdminUserReport(params: { start?: string; end?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();
    return this.request<{
      total: number;
      new: number;
      newChange: number;
      active: number;
      byTier: Record<string, number>;
      growth: Array<{ date: string; count: number }>;
      period: { start: string; end: string };
    }>(`/api/admin/reports/users${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Get AI operational costs report
   */
  async getAdminAICostsReport(params: { start?: string; end?: string } = {}) {
    const query = new URLSearchParams();
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);
    const queryString = query.toString();
    return this.request<{
      totals: { requests: number; tokens: number; cost: number; successRate: number };
      byProvider: Array<{ name: string; requests: number; tokens: number; cost: number; successRate: number }>;
      byUseCase: Array<{ name: string; requests: number; tokens: number; cost: number }>;
      daily: Array<{ date: string; cost: number }>;
      period: { start: string; end: string };
    }>(`/api/admin/reports/ai-costs${queryString ? `?${queryString}` : ''}`, { requireAuth: true });
  }

  /**
   * Export report data as CSV
   */
  async exportAdminReport(params: {
    report: 'revenue' | 'subscriptions' | 'users' | 'ai-costs';
    start?: string;
    end?: string;
  }): Promise<Blob> {
    const query = new URLSearchParams();
    query.set('report', params.report);
    if (params.start) query.set('start', params.start);
    if (params.end) query.set('end', params.end);

    const accessToken = await this.getToken();
    const response = await fetch(`${this.baseUrl}/api/admin/reports/export?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // =============================================================================
  // ADMIN DEV BUILDS ENDPOINTS
  // =============================================================================

  /**
   * Get all dev builds, optionally filtered by type
   */
  async getDevBuilds(buildType?: DevBuildType) {
    const query = buildType ? `?build_type=${buildType}` : '';
    return this.request<{
      builds:
        | {
            extension?: DevBuild[];
            'extension-chrome'?: DevBuild[];
            'extension-firefox'?: DevBuild[];
            'extension-safari'?: DevBuild[];
            android: DevBuild[];
            ios: DevBuild[];
          }
        | DevBuild[];
      total: number;
    }>(`/api/admin/builds${query}`, { requireAuth: true });
  }

  /**
   * Download a build file
   * Returns the file as a blob for downloading
   */
  async downloadDevBuild(buildId: string): Promise<{ blob: Blob; filename: string }> {
    const accessToken = await this.getToken();
    const response = await fetch(`${this.baseUrl}/api/admin/builds/${buildId}/download`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Download failed');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'download';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        filename = match[1];
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }

  /**
   * Get a signed download URL for a build (for mobile browsers)
   * Returns a time-limited URL that can be used for direct download
   */
  async getDevBuildDownloadUrl(buildId: string): Promise<{ url: string; filename: string; expires_in: number }> {
    const accessToken = await this.getToken();
    const response = await fetch(`${this.baseUrl}/api/admin/builds/${buildId}/download-url`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get download URL' }));
      throw new Error(error.error || 'Failed to get download URL');
    }

    return response.json();
  }

  /**
   * Upload a new dev build (super admin only)
   * Uses multipart upload for large files (> 50MB) to bypass Cloudflare Workers 100MB body limit
   */
  async uploadDevBuild(params: {
    file: File;
    version: string;
    buildType: DevBuildType;
    releaseNotes?: string;
    setAsLatest?: boolean;
  }) {
    const accessToken = await this.getToken();
    const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB
    const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks

    // Use multipart upload for large files
    if (params.file.size > LARGE_FILE_THRESHOLD) {
      // Step 1: Initialize multipart upload
      const initResponse = await fetch(`${this.baseUrl}/api/admin/builds/multipart/init`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: params.file.name,
          fileSize: params.file.size,
          buildType: params.buildType,
          version: params.version,
          releaseNotes: params.releaseNotes || '',
          setAsLatest: params.setAsLatest !== false,
        }),
      });

      if (!initResponse.ok) {
        const error = await initResponse.json().catch(() => ({ error: 'Failed to initialize upload' }));
        throw new Error(error.error || 'Failed to initialize upload');
      }

      const { uploadId, fileKey, metadata } = await initResponse.json();

      try {
        // Step 2: Upload parts
        const parts: Array<{ partNumber: number; etag: string }> = [];
        const totalParts = Math.ceil(params.file.size / CHUNK_SIZE);

        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          const start = (partNumber - 1) * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, params.file.size);
          const chunk = params.file.slice(start, end);

          const partResponse = await fetch(`${this.baseUrl}/api/admin/builds/multipart/part`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/octet-stream',
              'X-Upload-Id': uploadId,
              'X-File-Key': fileKey,
              'X-Part-Number': partNumber.toString(),
            },
            body: chunk,
          });

          if (!partResponse.ok) {
            throw new Error(`Failed to upload part ${partNumber}`);
          }

          const partResult = await partResponse.json();
          parts.push({ partNumber: partResult.partNumber, etag: partResult.etag });
        }

        // Step 3: Complete multipart upload
        const completeResponse = await fetch(`${this.baseUrl}/api/admin/builds/multipart/complete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uploadId,
            fileKey,
            parts,
            metadata,
          }),
        });

        if (!completeResponse.ok) {
          const error = await completeResponse.json().catch(() => ({ error: 'Failed to complete upload' }));
          throw new Error(error.error || 'Failed to complete upload');
        }

        return completeResponse.json() as Promise<DevBuild>;
      } catch (error) {
        // Abort on failure
        await fetch(`${this.baseUrl}/api/admin/builds/multipart/abort`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ uploadId, fileKey }),
        }).catch(() => {}); // Ignore abort errors
        throw error;
      }
    }

    // Use FormData for smaller files (original approach)
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('version', params.version);
    formData.append('build_type', params.buildType);
    if (params.releaseNotes) formData.append('release_notes', params.releaseNotes);
    if (params.setAsLatest) formData.append('set_as_latest', 'true');

    const response = await fetch(`${this.baseUrl}/api/admin/builds/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json() as Promise<DevBuild>;
  }

  /**
   * Delete a dev build (super admin only)
   */
  async deleteDevBuild(buildId: string) {
    return this.request<{ success: boolean }>(`/api/admin/builds/${buildId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  /**
   * Mark a build as latest (super admin only)
   */
  async setDevBuildAsLatest(buildId: string) {
    return this.request<DevBuild>(`/api/admin/builds/${buildId}/latest`, {
      method: 'PUT',
      requireAuth: true,
    });
  }

  /**
   * Update build metadata (super admin only)
   */
  async updateDevBuild(buildId: string, params: { version?: string; release_notes?: string }) {
    return this.request<DevBuild>(`/api/admin/builds/${buildId}`, {
      method: 'PUT',
      body: JSON.stringify(params),
      requireAuth: true,
    });
  }

  /**
   * Get TestFlight URL (public)
   */
  async getTestFlightUrl(): Promise<{ url: string | null }> {
    return this.request<{ url: string | null }>('/api/settings/testflight-url');
  }

  /**
   * Update TestFlight URL (admin only)
   */
  async setTestFlightUrl(url: string): Promise<{ success: boolean; url: string }> {
    return this.request<{ success: boolean; url: string }>('/api/settings/admin/testflight-url', {
      method: 'PUT',
      body: JSON.stringify({ url }),
      requireAuth: true,
    });
  }

  // =========================================================================
  // Dev Build API Keys (for automated uploads)
  // =========================================================================

  /**
   * Get all API keys (super admin only)
   */
  async getDevBuildApiKeys(): Promise<{ keys: DevBuildApiKey[] }> {
    return this.request<{ keys: DevBuildApiKey[] }>('/api/admin/builds/api-keys', {
      requireAuth: true,
    });
  }

  /**
   * Create a new API key (super admin only)
   * Returns the full key ONCE - it cannot be retrieved again!
   */
  async createDevBuildApiKey(name: string): Promise<DevBuildApiKeyCreated> {
    return this.request<DevBuildApiKeyCreated>('/api/admin/builds/api-keys', {
      method: 'POST',
      body: JSON.stringify({ name }),
      requireAuth: true,
    });
  }

  /**
   * Revoke an API key (super admin only)
   */
  async revokeDevBuildApiKey(keyId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/admin/builds/api-keys/${keyId}`, {
      method: 'DELETE',
      requireAuth: true,
    });
  }

  // ============================================
  // BUG REPORTS ENDPOINTS
  // ============================================

  /**
   * Submit a bug report
   */
  async submitBugReport(data: {
    category: BugReportCategory;
    description: string;
    systemInfo: Record<string, unknown>;
    screenshot?: string; // base64 encoded
    screenshotMimeType?: string;
    platform: 'web' | 'mobile' | 'extension' | 'admin';
    email?: string;
    detectedCountry?: string;
  }): Promise<{
    success: boolean;
    reportId: string;
    message: string;
  }> {
    return this.request<{
      success: boolean;
      reportId: string;
      message: string;
    }>('/api/bug-reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get user's own bug reports
   */
  async getUserBugReports(params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    reports: BugReport[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));

    const query = searchParams.toString();
    return this.request<{
      reports: BugReport[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/bug-reports${query ? `?${query}` : ''}`, {
      requireAuth: true,
    });
  }

  // ============================================
  // ADMIN BUG REPORTS
  // ============================================

  async getAdminBugReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    platform?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.limit) query.set('limit', params.limit.toString());
    if (params.status) query.set('status', params.status);
    if (params.category) query.set('category', params.category);
    if (params.platform) query.set('platform', params.platform);
    if (params.search) query.set('search', params.search);

    return this.request<{
      reports: BugReport[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/api/admin/bug-reports?${query.toString()}`, { requireAuth: true });
  }

  async getAdminBugReportStats() {
    return this.request<{
      total: number;
      byStatus: Record<string, number>;
      byCategory: Record<string, number>;
      byPlatform: Record<string, number>;
      newToday: number;
      newThisWeek: number;
    }>('/api/admin/bug-reports/stats', { requireAuth: true });
  }

  async getAdminBugReport(id: string) {
    return this.request<{
      report: BugReport;
    }>(`/api/admin/bug-reports/${id}`, { requireAuth: true });
  }

  async updateAdminBugReport(id: string, data: {
    status?: string;
    admin_notes?: string;
  }) {
    return this.request<{ success: boolean }>(`/api/admin/bug-reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  async analyzeAdminBugReports(data: {
    filters?: {
      status?: string[];
      category?: string[];
      platform?: string[];
    };
    analysisType?: 'summary' | 'patterns' | 'actionable' | 'all';
  }) {
    return this.request<{
      reportCount: number;
      analysis: {
        summary: string;
        commonPatterns: string[];
        affectedAreas: string[];
        recommendations: string[];
        priorityBugs: string[];
      };
    }>('/api/admin/bug-reports/analyze', {
      method: 'POST',
      body: JSON.stringify(data),
      requireAuth: true,
    });
  }

  // ============================================
  // STORE PREFERENCES ENDPOINTS
  // ============================================

  /**
   * Get all IKEA stores
   */
  async getAllStores(): Promise<{
    success: boolean;
    data: StoreDataResponse[];
  }> {
    return this.request<{
      success: boolean;
      data: StoreDataResponse[];
    }>('/api/stores');
  }

  /**
   * Get stores near a zipcode
   */
  async getNearbyStores(zipcode: string, maxDistance: number = 100): Promise<{
    success: boolean;
    data: NearbyStoresDataResponse;
  }> {
    return this.request<{
      success: boolean;
      data: NearbyStoresDataResponse;
    }>(`/api/stores/nearby?zipcode=${zipcode}&max_distance=${maxDistance}`);
  }

  /**
   * Get user's store preferences
   */
  async getStorePreferences(): Promise<{
    success: boolean;
    data: StorePreferencesDataResponse;
  }> {
    return this.request<{
      success: boolean;
      data: StorePreferencesDataResponse;
    }>('/api/stores/preferences', {
      requireAuth: true,
    });
  }

  /**
   * Update user's store preferences
   */
  async updateStorePreferences(prefs: {
    zipcode?: string;
    home_store_id?: string;
    max_distance_miles?: number;
    bulk_threshold?: number;
  }): Promise<{
    success: boolean;
    data: StorePreferencesDataResponse & {
      road_trip_store_count: number;
    };
  }> {
    return this.request<{
      success: boolean;
      data: StorePreferencesDataResponse & {
        road_trip_store_count: number;
      };
    }>('/api/stores/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
      requireAuth: true,
    });
  }

  /**
   * Get a single store by ID
   */
  async getStore(storeId: string): Promise<{
    success: boolean;
    data: StoreDataResponse;
  }> {
    return this.request<{
      success: boolean;
      data: StoreDataResponse;
    }>(`/api/stores/${storeId}`);
  }
}

// Bug report types
export type BugReportCategory =
  | 'display_issue'
  | 'crash_freeze'
  | 'data_content_error'
  | 'performance_problem'
  | 'feature_not_working'
  | 'login_account_issue'
  | 'label_scan_issue'
  | 'other';

export type BugReportStatus = 'new' | 'investigating' | 'resolved' | 'closed';

export interface BugReport {
  id: string;
  user_id: string | null;
  user_email: string | null;
  category: BugReportCategory;
  description: string;
  detected_language: string | null;
  description_english: string | null;
  was_translated: boolean;
  system_info: Record<string, unknown>;
  screenshot_url: string | null;
  screenshot_path: string | null;
  platform: 'web' | 'mobile' | 'extension' | 'admin';
  detected_country: string | null;
  status: BugReportStatus;
  admin_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

// Dev build types - supports browser-specific extensions
export type DevBuildType = 'extension' | 'extension-chrome' | 'extension-firefox' | 'extension-safari' | 'android' | 'ios';

export interface DevBuild {
  id: string;
  build_type: DevBuildType;
  version: string;
  release_notes: string | null;
  file_name: string;
  file_size: number;
  is_latest: boolean;
  download_count: number;
  created_at: string;
  uploaded_by: string;
  uploader_email?: string;
}

// API key for automated build uploads (never expires)
export interface DevBuildApiKey {
  id: string;
  name: string;
  key_prefix: string; // First 12 chars + "..." for identification
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  created_by: string;
}

// Returned only when creating a new key - includes full key
export interface DevBuildApiKeyCreated extends DevBuildApiKey {
  key: string; // Full key - only shown once on creation!
  message: string;
}

// Store data types
export interface StoreDataResponse {
  id: string;
  name: string;
  city: string;
  state: string;
  region: string;
  country: string;
  latitude: number;
  longitude: number;
  distance?: number;
}

export interface NearbyStoresDataResponse {
  user_coords: { lat: number; lon: number };
  home_store: StoreDataResponse | null;
  road_trip_stores: StoreDataResponse[];
  total_stores: number;
}

export interface StorePreferencesDataResponse {
  zipcode: string | null;
  home_store_id: string | null;
  max_distance_miles: number;
  road_trip_stores: string[];
  bulk_threshold: number;
  home_store: StoreDataResponse | null;
}

export const api = new ApiClient(API_URL);

// Export marketplace types
export type {
  MarketplaceListing,
  MarketplaceConversation,
  MarketplaceMessage,
  MarketplaceTransaction,
  MarketplaceRating,
  MarketplaceUserStats,
  MarketplacePreferences,
};

// Export SMCC types
export type {
  SMCCPlatform,
  SMCCContentType,
  SMCCContentStatus,
  SMCCAutomationLevel,
  SMCCMediaItem,
  SMCCContent,
  SMCCContentVersion,
  SMCCCampaignStatus,
  SMCCCampaignGoalType,
  SMCCCampaign,
  SMCCInboxItemType,
  SMCCSentiment,
  SMCCIntent,
  SMCCPriority,
  SMCCInboxItemStatus,
  SMCCInboxItem,
  SMCCSuggestedResponse,
  SMCCAutoResponseRule,
  SMCCModerationLog,
  SMCCAssetType,
  SMCCAsset,
  SMCCConsentStatus,
  SMCCUGCSubmission,
  SMCCComplianceRule,
  SMCCKillSwitch,
  SMCCCalendarItem,
  SMCCAnalyticsDaily,
  SMCCTopContent,
  SMCCExportDataItem,
  SMCCConversationMessage,
  SMCCBrandVoice,
};
